import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/pages/ChatPage.jsx
// Chat tipo Slack: permisos estrictos por tipo de canal, menciones contextuales,
// adjuntos/notas de voz, llamadas WebRTC y Web Push — todo en tiempo real.
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Alert, Avatar, Badge, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, FormControl, IconButton,
    InputLabel, LinearProgress, List, ListItem, ListItemAvatar, ListItemButton,
    ListItemText, ListSubheader, Menu, MenuItem, Paper, Popover, Popper, Select, Stack, TextField,
    Tooltip, Typography, ClickAwayListener, useTheme, useMediaQuery
} from '@mui/material';
import {
    Send as SendIcon, Add as AddIcon, Tag as TagIcon, AlternateEmail as AtIcon,
    DirectionsCar as VehicleIcon, ConfirmationNumber as TicketIcon, Assignment as TaskIcon,
    Sensors as SensorIcon, Router as ReceiverIcon, Business as CompanyIcon, Person as UserIcon,
    Tune as ProfileIcon, Refresh as RefreshIcon, Lock as LockIcon, Public as PublicIcon,
    Close as CloseIcon, Search as SearchIcon, FiberManualRecord as DotIcon,
    DeleteOutline as DeleteIcon, Group as GroupIcon, Badge as OperatorIcon,
    AttachFile as AttachIcon, Call as CallIcon, Videocam as VideoCallIcon,
    SupportAgent as SupportIcon, InsertDriveFile as FileIcon, PhoneInTalk as ActiveCallIcon,
    NotificationsOff as MutedIcon,
    AddReactionOutlined as ReactIcon, Reply as ReplyIcon,
    KeyboardArrowDown as ArrowDownIcon,
    Done as DoneIcon, DoneAll as DoneAllIcon, AccessTime as ClockIcon,
    ArrowBack as ArrowBackIcon, InfoOutlined as InfoOutlinedIcon,
    SmartToy as AssistantIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/common/GlobalNotifications';
import { useAuth } from '../hooks/useAuth';
import websocketService from '../services/websocketService';
import AttachmentItem from '../components/chat/AttachmentItem';
import VoiceRecorder from '../components/chat/VoiceRecorder';
import VideoNoteRecorder, { videoRecordingSupported } from '../components/chat/VideoNoteRecorder';
import CallOverlay, { IncomingCallDialog } from '../components/chat/CallOverlay';
import { ensurePushSubscription, initPushNavigation, requestPushPermissionExplicitly, pushPermissionState } from '../services/pushClient';
import soundManager from '../services/soundManager';
import chatPrefs from '../services/chatPrefs';
import ChatPreferencesDialog from '../components/chat/ChatPreferencesDialog';
import AssistantPanel from '../components/ai/AssistantPanel';
import ChannelMuteMenu from '../components/chat/ChannelMuteMenu';

// Canal virtual del Asistente APS (no es un canal real de la API de chat).
// Identificador fijo, no se traduce (es un ID interno, no una etiqueta).
const AI_CHANNEL_ID = '__aps_assistant__';

const ICON_FOR_TYPE = {
    user: UserIcon, vehicle: VehicleIcon, ticket: TicketIcon, task: TaskIcon,
    sensor: SensorIcon, receiver: ReceiverIcon, profile: ProfileIcon, company: CompanyIcon,
    operator: OperatorIcon,
};

// Mapeo entidad → ruta de navegación. Si null, no navega.
const ROUTE_FOR_MENTION = {
    user: null,
    operator: null,
    vehicle: (id) => `/vehicles/schematics?vehicle=${id}`,
    ticket: (id) => `/alarm-tickets/${id}`,
    task: (id) => `/tasks/${id}`,
    profile: () => `/alarm-profiles`,
    sensor: null,
    receiver: null,
    company: null,
};

// Etiquetas claras por tipo de canal (visibilidad).
// Se expone como funcion que recibe `t` para reaccionar a cambios de idioma.
const getChannelTypeMeta = (t) => ({
    custom: { label: t('hardcoded.pages_chatpage.canal_personalizado'), scope: t('hardcoded.pages_chatpage.solo_miembros_del_canal'), Icon: LockIcon, color: 'warning' },
    company: { label: t('hardcoded.pages_chatpage.canal_de_empresa'), scope: t('hardcoded.pages_chatpage.miembros_de_la_empresa'), Icon: CompanyIcon, color: 'info' },
    support: { label: t('hardcoded.pages_chatpage.soporte_arsenal'), scope: t('hardcoded.pages_chatpage.empresa_equipo_arsenal'), Icon: SupportIcon, color: 'error' },
    direct: { label: t('hardcoded.pages_chatpage.mensaje_directo'), scope: '1 a 1', Icon: UserIcon, color: 'default' },
    task: { label: t('hardcoded.pages_chatpage.canal_de_tarea'), scope: t('hardcoded.pages_chatpage.miembros_de_la_empresa'), Icon: TaskIcon, color: 'default' },
    ticket: { label: t('hardcoded.pages_chatpage.canal_de_ticket'), scope: t('hardcoded.pages_chatpage.miembros_de_la_empresa'), Icon: TicketIcon, color: 'default' },
    vehicle: { label: t('hardcoded.pages_chatpage.canal_de_vehiculo'), scope: t('hardcoded.pages_chatpage.miembros_de_la_empresa'), Icon: VehicleIcon, color: 'default' },
});

// En un chat directo cada participante ve al OTRO. El backend manda los dos
// miembros (`direct_members`) en vez de un nombre ya resuelto, porque el mismo
// payload viaja por WebSocket a ambos; aquí se elige el que toca. Todo lo que
// pinte el nombre de un canal debe pasar por aquí o los tres sitios acabarán
// mostrando cosas distintas.
const directCounterpart = (channel, currentUserId) => (
    (channel?.direct_members || []).find((m) => String(m.user_id) !== String(currentUserId)) || null
);

const channelTitle = (channel, currentUserId) => {
    if (!channel) return '';
    if (channel.channel_type !== 'direct') return channel.name;
    const otro = directCounterpart(channel, currentUserId);
    return otro?.name || otro?.email || channel.name;
};

const formatTime = (iso) => iso ? new Date(iso).toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '';
const formatDay = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    if (isToday) return i18n.t('hardcoded.pages_chatpage.hoy');
    if (isYesterday) return i18n.t('hardcoded.pages_chatpage.ayer');
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtSize = (b) => {
    if (b == null) return '';
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} ${i18n.t('hardcoded.pages_chatpage.kb')}`;
    return `${(b / (1024 * 1024)).toFixed(1)} ${i18n.t('hardcoded.pages_chatpage.mb')}`;
};

// Set rápido de reacciones (estilo WhatsApp/Slack premium).
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
// Set extendido para el picker "más".
const EXTRA_REACTIONS = ['🔥', '🎉', '👏', '🙌', '😍', '🤔', '😎', '✅', '❌', '💯', '🚀', '👀'];

// Paleta de colores por usuario para avatares (determinista por id/nombre).
const AVATAR_COLORS = [
    '#d32f2f', '#1976d2', '#388e3c', '#f57c00', '#7b1fa2',
    '#0097a7', '#c2185b', '#5d4037', '#455a64', '#00796b',
];
const colorForUser = (key) => {
    const s = String(key || '');
    let h = 0;
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const initialsFor = (name) => {
    const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Genera un id único de cliente para idempotencia (anti doble envío).
// Usa crypto.randomUUID() si está disponible, con fallback razonable.
const genClientMessageId = () => {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    } catch { /* noop */ }
    return `cmid-${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
};

// Autocomplete de menciones — categorizado por modulo.
// Se expone como funcion que recibe `t` para reaccionar a cambios de idioma.
const getMentionCategories = (t) => [
    { key: 'all', label: t('components_common_sidebar.todos') },
    { key: 'user', label: t('users.title') },
    { key: 'operator', label: t('components_common_sidebar.operadores') },
    { key: 'company', label: t('pages_chatanalyticspage.empresas') },
    { key: 'vehicle', label: t('vehicles.title') },
    { key: 'ticket', label: t('components_common_sidebar.tickets') },
    { key: 'task', label: t('pages_chatpage.tareas') },
    { key: 'profile', label: t('components_common_sidebar.perfiles') },
    { key: 'sensor', label: t('sensors.title') },
    { key: 'receiver', label: t('receivers.title') },
];

const MentionAutocomplete = ({ open, anchorEl, query, channelId, onPick, onClose }) => {
  const { t } = useTranslation();
  const categories = useMemo(() => getMentionCategories(t), [t]);
    const [groups, setGroups] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [category, setCategory] = useState('all');
    const [activeIndex, setActiveIndex] = useState(0);
    const categoriesRef = useRef(null);

    useEffect(() => { if (!open) { setCategory('all'); setActiveIndex(0); } }, [open]);

    useEffect(() => {
        if (!open) return;
        let cancel = false;
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const q = query && query.length > 0 ? query : 'a';
                if (category === 'all') {
                    const r = await api.chat.autocompleteGrouped(q, 5, channelId);
                    if (!cancel) { setGroups(r.data?.groups || []); setItems([]); }
                } else {
                    const r = await api.chat.autocomplete(q, category, 15, channelId);
                    if (!cancel) { setItems(r.data || []); setGroups([]); }
                }
            } catch { /* silent */ } finally { if (!cancel) setLoading(false); }
        }, 120);
        return () => { cancel = true; clearTimeout(t); };
    }, [query, open, category, channelId]);

    const flatItems = useMemo(
        () => (category === 'all' ? groups.flatMap((g) => g.items || []) : items),
        [category, groups, items],
    );
    useEffect(() => { setActiveIndex(0); }, [query, category, flatItems.length]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault(); e.stopPropagation();
                const idx = categories.findIndex((c) => c.key === category);
                const n = categories.length;
                const next = e.key === 'ArrowRight' ? (idx + 1) % n : (idx - 1 + n) % n;
                setCategory(categories[next].key);
            } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                if (!flatItems.length) return;
                e.preventDefault(); e.stopPropagation();
                setActiveIndex((i) => {
                    const n = flatItems.length;
                    return e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n;
                });
            } else if (e.key === 'Enter') {
                if (flatItems.length) {
                    e.preventDefault(); e.stopPropagation();
                    const it = flatItems[Math.min(activeIndex, flatItems.length - 1)];
                    if (it) onPick(it);
                }
            } else if (e.key === 'Escape') {
                e.preventDefault(); e.stopPropagation(); onClose();
            }
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [open, category, flatItems, activeIndex, onPick, onClose]);

    // Mantener la categoría activa visible en el scroll horizontal
    useEffect(() => {
        const el = categoriesRef.current?.querySelector('[data-active="true"]');
        if (el) el.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
    }, [category]);

    const total = flatItems.length;
    const activeLabel = categories.find((c) => c.key === category)?.label || '';

    const renderItem = (it, globalIdx) => {
        const Ico = ICON_FOR_TYPE[it.type] || AtIcon;
        const selected = globalIdx === activeIndex;
        return (
            <ListItemButton key={`${it.type}-${it.id}`} selected={selected}
                onMouseEnter={() => setActiveIndex(globalIdx)}
                onMouseDown={(e) => { e.preventDefault(); onPick(it); }}
                sx={{ py: 0.75, '&.Mui-selected': { bgcolor: 'rgba(211,47,47,0.08)' } }}>
                <ListItemAvatar sx={{ minWidth: 34 }}>
                    <Avatar sx={{ width: 26, height: 26, bgcolor: selected ? 'primary.main' : 'grey.200', color: selected ? '#fff' : 'text.secondary' }}>
                        <Ico sx={{ fontSize: 15 }} />
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    primary={<Typography variant="body2" sx={{ fontWeight: selected ? 700 : 500 }}>{it.label}</Typography>}
                    secondary={it.subtitle ? <Typography variant="caption" color="text.secondary" noWrap>{it.subtitle}</Typography> : null}
                />
            </ListItemButton>
        );
    };

    let runningIdx = -1;

    return (
        <Popper open={open && !!anchorEl} anchorEl={anchorEl} placement="top-start"
            sx={{ zIndex: 2000 }} modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}>
            <ClickAwayListener onClickAway={onClose}>
                <Paper elevation={8} sx={{ width: { xs: '92vw', sm: 440 }, maxWidth: 440, maxHeight: 420, display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                            {i18n.t('hardcoded.pages_chatpage.mencionar')}{query ? ` "${query}"` : ''} {t('hardcoded.pages_chatpage.solo_dentro_de_este_canal')}</Typography>
                        {!loading && total > 0 && (
                            <Typography variant="caption" color="text.secondary">{total} {i18n.t('hardcoded.pages_chatpage.resultado')}{total !== 1 ? 's' : ''}</Typography>
                        )}
                    </Box>

                    {/* Menú HORIZONTAL de categorías */}
                    <Box ref={categoriesRef} sx={{
                        display: 'flex', gap: 0.5, px: 1, py: 0.75, overflowX: 'auto',
                        borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
                        scrollbarWidth: 'thin', '&::-webkit-scrollbar': { height: 4 },
                        '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 2 },
                    }}>
                        {categories.map((c) => {
                            const isSel = c.key === category;
                            return (
                                <Chip
                                    key={c.key}
                                    data-active={isSel ? 'true' : 'false'}
                                    label={c.label}
                                    size="small"
                                    clickable
                                    onMouseDown={(e) => { e.preventDefault(); setCategory(c.key); }}
                                    sx={{
                                        flex: '0 0 auto',
                                        fontWeight: isSel ? 700 : 500,
                                        bgcolor: isSel ? 'primary.main' : 'grey.100',
                                        color: isSel ? '#fff' : 'text.primary',
                                        border: '1px solid',
                                        borderColor: isSel ? 'primary.main' : 'grey.300',
                                        '&:hover': { bgcolor: isSel ? 'primary.dark' : 'grey.200' },
                                    }}
                                />
                            );
                        })}
                    </Box>

                    <Box sx={{ overflowY: 'auto', flex: 1 }}>
                        {loading && <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}><CircularProgress size={18} /></Box>}

                        {!loading && total === 0 && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {category === 'all'
                                        ? t('hardcoded.pages_chatpage.sin_resultados_en_este_canal')
                                        : t('hardcoded.pages_chatpage.sin_resultados_en_activelabel_para', { activeLabel: activeLabel })}
                                </Typography>
                            </Box>
                        )}

                        {!loading && category === 'all' && groups.map((g) => (
                            <Box key={g.type}>
                                <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: 'primary.main' }}>
                                        {g.label}
                                    </Typography>
                                    <Chip size="small" label={g.items?.length || 0} sx={{ height: 16, fontSize: '0.62rem', bgcolor: 'grey.200' }} />
                                </Box>
                                <List dense disablePadding>
                                    {(g.items || []).map((it) => { runningIdx += 1; return renderItem(it, runningIdx); })}
                                </List>
                            </Box>
                        ))}

                        {!loading && category !== 'all' && (
                            <List dense disablePadding>
                                {items.map((it) => { runningIdx += 1; return renderItem(it, runningIdx); })}
                            </List>
                        )}
                    </Box>
                </Paper>
            </ClickAwayListener>
        </Popper>
    );
};

// ============ MemberPicker para canales privados ============
const MemberPicker = ({ value = [], onChange }) => {
  const { t } = useTranslation();
    const [q, setQ] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!q || q.length < 1) { setItems([]); return; }
        let cancel = false;
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const r = await api.chat.autocomplete(q, 'user', 10);
                if (!cancel) setItems(r.data || []);
            } catch { /* silent */ } finally { setLoading(false); }
        }, 200);
        return () => { cancel = true; clearTimeout(t); };
    }, [q]);

    const add = (it) => {
        if (value.find((v) => v.id === it.id)) return;
        onChange([...value, it]);
        setQ('');
        setItems([]);
    };
    const remove = (id) => onChange(value.filter((v) => v.id !== id));

    return (
        <Box>
            <TextField
                fullWidth size="small" placeholder={t('hardcoded.pages_chatpage.buscar_usuario_por_nombre_o')}
                value={q} onChange={(e) => setQ(e.target.value)}
                InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
            {loading && <CircularProgress size={16} sx={{ mt: 1 }} />}
            {!!items.length && (
                <Paper variant="outlined" sx={{ mt: 0.5, maxHeight: 180, overflowY: 'auto' }}>
                    <List dense>
                        {items.map((it) => (
                            <ListItemButton key={it.id} onClick={() => add(it)}>
                                <ListItemAvatar sx={{ minWidth: 36 }}><UserIcon fontSize="small" /></ListItemAvatar>
                                <ListItemText primary={it.label} secondary={it.subtitle} />
                            </ListItemButton>
                        ))}
                    </List>
                </Paper>
            )}
            {!!value.length && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    {value.map((v) => (
                        <Chip key={v.id} size="small" label={v.label} onDelete={() => remove(v.id)} />
                    ))}
                </Stack>
            )}
        </Box>
    );
};

// ============ Dialog de mensaje directo ============
// Un directo no se "crea": se abre. Si ya existe la conversación con esa
// persona se entra a la misma, así que aquí solo se elige a quién.
const DirectChatDialog = ({ open, onClose, onOpened, currentUserId }) => {
    const { t } = useTranslation();
    const { showError } = useNotification();
    const [q, setQ] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [abriendo, setAbriendo] = useState(null);

    useEffect(() => {
        if (open) { setQ(''); setItems([]); setAbriendo(null); }
    }, [open]);

    useEffect(() => {
        if (!q.trim()) { setItems([]); return; }
        let cancelado = false;
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const r = await api.chat.autocomplete(q, 'user', 15);
                // El buscador de menciones puede devolverte a ti mismo; contigo
                // no hay directo que abrir.
                if (!cancelado) setItems((r.data || []).filter((it) => String(it.id) !== String(currentUserId)));
            } catch { /* la búsqueda es asistencia: su fallo no bloquea el diálogo */ }
            finally { if (!cancelado) setLoading(false); }
        }, 250);
        return () => { cancelado = true; clearTimeout(timer); };
    }, [q, currentUserId]);

    const abrir = async (item) => {
        setAbriendo(item.id);
        try {
            const r = await api.chat.openDirect(item.id);
            onOpened?.(r.data);
            onClose();
        } catch (e) {
            showError(e?.response?.data?.detail || 'No se pudo abrir el chat directo');
        } finally { setAbriendo(null); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{t('hardcoded.pages_chatpage.mensaje_directo')}</DialogTitle>
            <DialogContent dividers>
                <TextField
                    fullWidth size="small" autoFocus
                    placeholder={t('hardcoded.pages_chatpage.buscar_usuario_por_nombre_o')}
                    value={q} onChange={(e) => setQ(e.target.value)}
                    InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
                {loading && <CircularProgress size={16} sx={{ mt: 1.5 }} />}
                {!loading && !!q.trim() && items.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                        {t('hardcoded.pages_chatpage.sin_resultados_directo')}
                    </Typography>
                )}
                {!!items.length && (
                    <List dense sx={{ mt: 0.5, maxHeight: 300, overflowY: 'auto' }}>
                        {items.map((it) => (
                            <ListItemButton key={it.id} onClick={() => abrir(it)} disabled={!!abriendo}>
                                <ListItemAvatar sx={{ minWidth: 36 }}>
                                    {abriendo === it.id ? <CircularProgress size={18} /> : <UserIcon fontSize="small" />}
                                </ListItemAvatar>
                                <ListItemText primary={it.label} secondary={it.subtitle} />
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
            </DialogActions>
        </Dialog>
    );
};

// ============ Dialog de crear canal ============
const ChannelCreateDialog = ({ open, onClose, onCreated, companyId }) => {
  const { t } = useTranslation();
    const { showError, showSuccess } = useNotification();
    const [form, setForm] = useState({ name: '', description: '', channel_type: 'custom' });
    const [members, setMembers] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({ name: '', description: '', channel_type: 'custom' });
            setMembers([]);
        }
    }, [open]);

    const isCustom = form.channel_type === 'custom';
    const isCompany = form.channel_type === 'company';
    const isSupport = form.channel_type === 'support';

    const typeHelp = useMemo(() => {
        if (isCustom) return t('hardcoded.pages_chatpage.privado_solo_los_miembros_que');
        if (isCompany) return t('hardcoded.pages_chatpage.visible_solo_para_los_usuarios');
        if (isSupport) return t('hardcoded.pages_chatpage.canal_de_soporte_visible_para');
        return '';
    }, [isCustom, isCompany, isSupport]);

    const save = async () => {
        if (!form.name.trim()) { showError(t('hardcoded.pages_chatpage.nombre_requerido')); return; }
        if (isCustom && members.length === 0) {
            showError(t('hardcoded.pages_chatpage.selecciona_al_menos_un_usuario'));
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                company_id: companyId || undefined,
                is_private: isCustom,
                member_user_ids: isCustom ? members.map((m) => m.id) : [],
            };
            const r = await api.chat.createChannel(payload);
            showSuccess(t('hardcoded.pages_chatpage.canal_creado'));
            onCreated?.(r.data);
            onClose();
        } catch (e) {
            showError(e?.response?.data?.detail || 'Error');
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{i18n.t('hardcoded.pages_chatpage.nuevo_canal')}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <TextField label={t('hardcoded.pages_chatpage.nombre_del_canal')} value={form.name}
                        onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} autoFocus />
                    <TextField label={t('hardcoded.pages_chatpage.descripcion_opcional')} multiline rows={2} value={form.description}
                        onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
                    <FormControl size="small">
                        <InputLabel>{i18n.t('vehicles.form.type')}</InputLabel>
                        <Select label={t('vehicles.form.type')} value={form.channel_type}
                            onChange={(e) => setForm((p) => ({ ...p, channel_type: e.target.value }))}>
                            <MenuItem value="custom">{i18n.t('hardcoded.pages_chatpage.canal_personalizado_privado_eliges_miembros')}</MenuItem>
                            <MenuItem value="company">{t('hardcoded.pages_chatpage.canal_de_empresa_todos_los')}</MenuItem>
                            <MenuItem value="support">{i18n.t('hardcoded.pages_chatpage.soporte_arsenal_empresa_equipo_arsenal')}</MenuItem>
                        </Select>
                    </FormControl>

                    <Alert severity={isCustom ? 'warning' : 'info'} icon={isCustom ? <LockIcon /> : <PublicIcon />} sx={{ py: 0.5 }}>
                        {typeHelp}
                    </Alert>

                    {isCustom && (
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                <GroupIcon fontSize="small" color="primary" />
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                    {t('hardcoded.pages_chatpage.miembros_con_acceso')}{members.length})
                                </Typography>
                            </Stack>
                            <MemberPicker value={members} onChange={setMembers} />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {t('hardcoded.pages_chatpage.tu_seras_incluido_automaticamente_como')}</Typography>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={saving}>
                    {saving ? <CircularProgress size={16} /> : t('hardcoded.pages_chatpage.crear_canal')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ============ Render del body con menciones legibles ============
// Chips de mención con contraste garantizado en ambos lados de la conversación:
// fondo claro + texto oscuro + borde/acento por color del tipo.
const renderMessageBody = (body, mentions, onNavigate) => {
    if (!body) return null;
    const safeMentions = Array.isArray(mentions) ? mentions : [];
    if (!safeMentions.length) {
        return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.55 }}>{body}</Typography>;
    }
    const sorted = [...safeMentions]
        .filter((m) => m && m.label)
        .map((m) => ({
            ...m,
            start: Number.isInteger(m.start) ? m.start : null,
            end: Number.isInteger(m.end) ? m.end : null,
        }));

    const useIndex = sorted.every((m) => m.start !== null && m.end !== null);
    const parts = [];
    if (useIndex) {
        sorted.sort((a, b) => a.start - b.start);
        let cursor = 0;
        for (const m of sorted) {
            if (m.start < cursor) continue;
            if (m.start > cursor) parts.push({ text: body.slice(cursor, m.start) });
            parts.push({ mention: m });
            cursor = m.end;
        }
        if (cursor < body.length) parts.push({ text: body.slice(cursor) });
    } else {
        let working = body;
        const pieces = [];
        for (const m of sorted) {
            const needle = `@${m.label}`;
            const idx = working.indexOf(needle);
            if (idx === -1) continue;
            if (idx > 0) pieces.push({ text: working.slice(0, idx) });
            pieces.push({ mention: m });
            working = working.slice(idx + needle.length);
        }
        if (working.length) pieces.push({ text: working });
        parts.push(...pieces);
    }

    return (
        <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.7 }}>
            {parts.map((p, i) => {
                if (p.text != null) return <span key={i}>{p.text}</span>;
                const m = p.mention;
                const Ico = ICON_FOR_TYPE[m.type] || AtIcon;
                const navFn = ROUTE_FOR_MENTION[m.type];
                const clickable = !!navFn;
                return (
                    <Chip
                        key={i}
                        size="small"
                        icon={<Ico style={{ fontSize: 13, color: '#b71c1c' }} />}
                        label={`@${m.label}`}
                        clickable={clickable}
                        onClick={clickable ? () => onNavigate(navFn(m.id)) : undefined}
                        sx={{
                            mx: 0.25, height: 21, fontSize: '0.74rem', fontWeight: 700,
                            // Contraste garantizado sobre cualquier burbuja:
                            bgcolor: 'rgba(211,47,47,0.10)',
                            color: '#8e1313',
                            border: '1px solid rgba(211,47,47,0.35)',
                            cursor: clickable ? 'pointer' : 'default',
                            verticalAlign: 'middle',
                            '&:hover': clickable ? { bgcolor: 'rgba(211,47,47,0.2)' } : undefined,
                        }}
                    />
                );
            })}
        </Typography>
    );
};

// ============ Picker compacto de reacciones ============
const ReactionPicker = ({ open, anchorEl, onPick, onClose, isOwn }) => {
  const { t } = useTranslation();
    const [showExtra, setShowExtra] = useState(false);
    useEffect(() => { if (!open) setShowExtra(false); }, [open]);
    return (
        <Popper open={open && !!anchorEl} anchorEl={anchorEl} placement={isOwn ? 'top-end' : 'top-start'}
            sx={{ zIndex: 2100 }} modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}>
            <ClickAwayListener onClickAway={onClose}>
                <Paper
                    component={motion.div}
                    initial={{ opacity: 0, scale: 0.85, y: 6 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.14 }}
                    elevation={8}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 0.25, p: 0.5,
                        borderRadius: 6, border: '1px solid', borderColor: 'divider',
                        maxWidth: { xs: '92vw', sm: 360 }, flexWrap: 'wrap',
                    }}
                >
                    {(showExtra ? [...QUICK_REACTIONS, ...EXTRA_REACTIONS] : QUICK_REACTIONS).map((emoji) => (
                        <IconButton key={emoji} size="small"
                            onClick={() => { onPick(emoji); onClose(); }}
                            sx={{
                                fontSize: '1.3rem', width: 40, height: 40, transition: 'transform .1s',
                                '&:hover': { transform: 'scale(1.25)', bgcolor: 'action.hover' },
                            }}>
                            {emoji}
                        </IconButton>
                    ))}
                    {!showExtra && (
                        <IconButton size="small" onClick={() => setShowExtra(true)}
                            sx={{ width: 40, height: 40, color: 'text.secondary' }}>
                            <AddIcon fontSize="small" />
                        </IconButton>
                    )}
                </Paper>
            </ClickAwayListener>
        </Popper>
    );
};

// ============ Chips de reacciones bajo la burbuja ============
const ReactionChips = ({ reactions, currentUserId, onToggle, align }) => {
  const { t } = useTranslation();
    if (!Array.isArray(reactions) || !reactions.length) return null;
    return (
        <Stack direction="row" spacing={0.5} sx={{
            mt: 0.4, flexWrap: 'wrap', gap: 0.4,
            justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        }}>
            {reactions.filter((r) => r && r.emoji && (r.count > 0)).map((r) => {
                const mine = Array.isArray(r.user_ids) && r.user_ids.some((uid) => String(uid) === String(currentUserId));
                return (
                    <Box
                        key={r.emoji}
                        component={motion.button}
                        type="button"
                        initial={{ scale: 0.6, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 28 }}
                        onClick={() => onToggle(r.emoji)}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.4,
                            px: { xs: 1, md: 0.75 }, py: 0.1,
                            height: { xs: 28, md: 22 }, cursor: 'pointer',
                            borderRadius: 11, fontSize: '0.78rem', lineHeight: 1,
                            border: '1px solid',
                            borderColor: mine ? 'primary.main' : 'divider',
                            bgcolor: mine ? 'rgba(211,47,47,0.12)' : 'background.paper',
                            color: 'text.primary', fontWeight: 600,
                            transition: 'background-color .12s, border-color .12s',
                            '&:hover': { bgcolor: mine ? 'rgba(211,47,47,0.2)' : 'action.hover' },
                        }}
                    >
                        <span>{r.emoji}</span>
                        <Box component="span" sx={{ fontSize: '0.7rem', color: mine ? 'primary.main' : 'text.secondary', fontWeight: 700 }}>
                            {r.count}
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    );
};

// ============ Bloque de cita dentro de la burbuja (estilo WhatsApp) ============
const QuotedBlock = ({ reply, isOwn, onJump }) => {
  const { t } = useTranslation();
    if (!reply) return null;
    const deleted = !!reply.deleted;
    const author = reply.user_name || t('audit.table.user');
    const extract = deleted ? t('hardcoded.pages_chatpage.mensaje_eliminado') : (reply.body || t('hardcoded.pages_chatpage.adjunto'));
    return (
        <Box
            onClick={() => !deleted && onJump?.(reply.id)}
            sx={{
                mb: 0.6, px: 1, py: 0.5, borderRadius: 1.5,
                borderLeft: '3px solid',
                borderColor: isOwn ? 'primary.main' : 'secondary.main',
                bgcolor: isOwn ? 'rgba(211,47,47,0.06)' : 'rgba(0,0,0,0.04)',
                cursor: deleted ? 'default' : 'pointer',
                maxWidth: '100%', overflow: 'hidden',
                transition: 'background-color .12s',
                '&:hover': deleted ? {} : { bgcolor: isOwn ? 'rgba(211,47,47,0.12)' : 'rgba(0,0,0,0.07)' },
            }}
        >
            <Typography variant="caption" sx={{ fontWeight: 800, color: isOwn ? 'primary.main' : 'secondary.main', display: 'block', lineHeight: 1.2 }}>
                {author}
            </Typography>
            <Typography variant="caption" sx={{
                color: deleted ? 'text.disabled' : 'text.secondary',
                fontStyle: deleted ? 'italic' : 'normal',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                overflow: 'hidden', wordBreak: 'break-word', lineHeight: 1.3,
            }}>
                {extract}
            </Typography>
        </Box>
    );
};

// ============ Recibos estilo WhatsApp (✓ enviado / ✓✓ entregado / ✓✓ azul visto) ============
// Color del tick "visto" (azul WhatsApp).
const SEEN_BLUE = '#34B7F1';

// Convierte ISO a epoch ms (o 0 si null/invalid). Comparable de forma estable.
const ts = (iso) => {
    if (!iso) return 0;
    const t = Date.parse(iso);
    return Number.isNaN(t) ? 0 : t;
};

// Calcula el estado de un mensaje PROPIO frente a los OTROS miembros del canal.
// receiptsByUser: { user_id -> {last_delivered_at, last_read_at} } (ya SIN mi user_id).
// otherIds: array de user_ids de los demás miembros (de receipts, excluido yo).
// Devuelve: 'sending' | 'sent' | 'delivered' | 'seen'.
const computeMessageStatus = (message, receiptsByUser, otherIds) => {
    // Optimista: aún sin id del servidor o marcado __sending.
    if (message.__sending || !message.id) return 'sending';
    // Sin id de creación no podemos comparar fechas: como mínimo "enviado".
    const createdMs = ts(message.created_at);
    if (!createdMs || !otherIds || otherIds.length === 0) return 'sent';

    let allDelivered = true;
    let allRead = true;
    for (const uid of otherIds) {
        const r = receiptsByUser[uid] || {};
        if (ts(r.last_delivered_at) < createdMs && ts(r.last_read_at) < createdMs) allDelivered = false;
        if (ts(r.last_read_at) < createdMs) allRead = false;
        if (!allDelivered && !allRead) break;
    }
    if (allRead) return 'seen';
    if (allDelivered) return 'delivered';
    return 'sent';
};

// Icono discreto del estado, junto al timestamp. Solo se usa en mensajes propios.
const MessageStatusTick = ({ status, onClick }) => {
  const { t } = useTranslation();
    const common = { fontSize: '0.95rem', verticalAlign: 'middle' };
    let icon;
    if (status === 'sending') {
        icon = <ClockIcon sx={{ ...common, color: 'text.disabled' }} />;
    } else if (status === 'sent') {
        icon = <DoneIcon sx={{ ...common, color: 'text.disabled' }} />;
    } else if (status === 'delivered') {
        icon = <DoneAllIcon sx={{ ...common, color: 'text.disabled' }} />;
    } else if (status === 'seen') {
        icon = <DoneAllIcon sx={{ ...common, color: SEEN_BLUE }} />;
    } else {
        return null;
    }
    return (
        <Box
            component="span"
            onClick={onClick}
            sx={{ display: 'inline-flex', ml: 0.4, cursor: onClick ? 'pointer' : 'default' }}
            aria-label={t('hardcoded.pages_chatpage.estado_status', { status: status })}
        >
            {icon}
        </Box>
    );
};

// Contenido del popover "Info del mensaje": por cada OTRO miembro muestra la hora
// de Entregado y Visto frente al created_at del mensaje. Útil sobre todo en grupos.
const MessageInfoContent = ({ message, receipts, otherIds }) => {
  const { t } = useTranslation();
    const createdMs = ts(message?.created_at);
    return (
        <Box sx={{ p: 1.5, minWidth: 240, maxWidth: 320 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {t('hardcoded.pages_chatpage.info_del_mensaje')}</Typography>
            {(!otherIds || otherIds.length === 0) && (
                <Typography variant="caption" color="text.secondary">
                    {t('hardcoded.pages_chatpage.sin_otros_miembros_con_recibos')}</Typography>
            )}
            <List dense disablePadding>
                {(otherIds || []).map((uid) => {
                    const r = receipts[uid] || {};
                    const delivered = ts(r.last_delivered_at) >= createdMs || ts(r.last_read_at) >= createdMs;
                    const read = ts(r.last_read_at) >= createdMs;
                    return (
                        <ListItem key={uid} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
                            <ListItemText
                                primary={r.user_name || t('hardcoded.pages_chatpage.usuario_uid', { uid: uid })}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                                secondary={
                                    <Box component="span" sx={{ display: 'block' }}>
                                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <DoneAllIcon sx={{ fontSize: '0.9rem', color: 'text.disabled' }} />
                                            {i18n.t('hardcoded.pages_chatpage.entregado')}{delivered ? formatTime(read ? r.last_read_at : r.last_delivered_at) : '—'}
                                        </Box>
                                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <DoneAllIcon sx={{ fontSize: '0.9rem', color: SEEN_BLUE }} />
                                            {i18n.t('hardcoded.pages_chatpage.visto')}{read ? formatTime(r.last_read_at) : '—'}
                                        </Box>
                                    </Box>
                                }
                                secondaryTypographyProps={{ component: 'span', variant: 'caption' }}
                            />
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );
};

// ============ Burbuja de mensaje ============
// FIX de legibilidad: los mensajes PROPIOS usan fondo tenue rojo con TEXTO
// OSCURO (antes: rojo sólido con elementos ilegibles encima).
// Memoizado: solo re-renderiza si cambian sus props (mensaje, reacciones, etc.).
const MessageBubble = React.memo(function MessageBubble({
    message, isOwn, showAvatar, showName, onNavigate,
    currentUserId, onReact, onReply, onJump, highlighted,
    messageStatus, onShowInfo, isMobile,
}) {
  const { t } = useTranslation();
    const userName = message.user_name || message.user_email || t('audit.table.user');
    const attachments = message.attachments || [];
    const [hover, setHover] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    // Menú táctil de acciones (móvil): se ancla en la burbuja con tap/long-press.
    const [touchMenuAnchor, setTouchMenuAnchor] = useState(null);
    const reactBtnRef = useRef(null);
    const bubbleRef = useRef(null);
    const longPressTimerRef = useRef(null);
    const sending = message.__sending;

    // En táctil: long-press (550ms) abre el menú de acciones anclado a la burbuja.
    const handleTouchStart = () => {
        if (!isMobile) return;
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = setTimeout(() => {
            if (bubbleRef.current) setTouchMenuAnchor(bubbleRef.current);
        }, 550);
    };
    const cancelLongPress = () => clearTimeout(longPressTimerRef.current);
    // Tap simple en móvil también abre el menú (si no se está enviando).
    const handleBubbleClick = () => {
        if (!isMobile || sending) return;
        clearTimeout(longPressTimerRef.current);
        if (bubbleRef.current) setTouchMenuAnchor(bubbleRef.current);
    };
    useEffect(() => () => clearTimeout(longPressTimerRef.current), []);

    const closeTouchMenu = () => setTouchMenuAnchor(null);

    // Barra de acciones por hover (solo desktop).
    const actionBar = !isMobile && (
        <Stack direction="row" spacing={0.25} sx={{
            opacity: hover || pickerOpen ? 1 : 0,
            transition: 'opacity .12s',
            alignSelf: 'center',
            pointerEvents: (hover || pickerOpen) ? 'auto' : 'none',
        }}>
            <Tooltip title={t('hardcoded.pages_chatpage.reaccionar')}>
                <IconButton ref={reactBtnRef} size="small" onClick={() => setPickerOpen((o) => !o)}
                    sx={{ width: 28, height: 28, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
                    <ReactIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
            <Tooltip title={t('hardcoded.pages_chatpage.responder')}>
                <IconButton size="small" onClick={() => onReply?.(message)}
                    sx={{ width: 28, height: 28, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
                    <ReplyIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
        </Stack>
    );

    return (
        <Box
            id={`msg-${message.id}`}
            component={motion.div}
            initial={message.__entering ? { opacity: 0, y: 10 } : false}
            animate={{ opacity: sending ? 0.65 : 1, y: 0 }}
            transition={{ duration: 0.18 }}
            onMouseEnter={isMobile ? undefined : () => setHover(true)}
            onMouseLeave={isMobile ? undefined : () => setHover(false)}
            sx={{
                display: 'flex',
                flexDirection: isOwn ? 'row-reverse' : 'row',
                alignItems: 'flex-end',
                gap: 1,
                mb: showAvatar ? 0.8 : 0.25,
                borderRadius: 2,
                transition: 'background-color .4s',
                bgcolor: highlighted ? 'rgba(211,47,47,0.12)' : 'transparent',
            }}>
            <Box sx={{ width: 32, flexShrink: 0 }}>
                {showAvatar && (
                    <Avatar sx={{ width: 32, height: 32, bgcolor: colorForUser(message.user_id || userName), fontSize: '0.8rem', fontWeight: 700 }}>
                        {initialsFor(userName)}
                    </Avatar>
                )}
            </Box>
            <Box sx={{ maxWidth: { xs: '85%', md: '72%' }, minWidth: 0 }}>
                {showName && !isOwn && (
                    <Typography variant="caption" sx={{ ml: 1.2, mb: 0.2, display: 'block', fontWeight: 700, color: colorForUser(message.user_id || userName) }}>
                        {userName}
                    </Typography>
                )}
                <Box
                    ref={bubbleRef}
                    onClick={handleBubbleClick}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={cancelLongPress}
                    onTouchMove={cancelLongPress}
                    onContextMenu={isMobile ? (e) => { e.preventDefault(); if (bubbleRef.current) setTouchMenuAnchor(bubbleRef.current); } : undefined}
                    sx={{
                    px: 1.5, py: 1,
                    borderRadius: 2,
                    cursor: isMobile ? 'pointer' : 'default',
                    WebkitTapHighlightColor: 'transparent',
                    bgcolor: isOwn ? '#fdeceb' : 'background.paper',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: isOwn ? 'rgba(211,47,47,0.45)' : 'divider',
                    borderRightWidth: isOwn ? 3 : 1,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    borderBottomRightRadius: isOwn ? 4 : 16,
                    borderBottomLeftRadius: isOwn ? 16 : 4,
                }}>
                    <QuotedBlock reply={message.reply_to} isOwn={isOwn} onJump={onJump} />
                    {renderMessageBody(message.body, message.mentions, onNavigate)}
                    {attachments.map((a) => <AttachmentItem key={a.id} attachment={a} />)}
                </Box>
                <ReactionChips
                    reactions={message.reactions}
                    currentUserId={currentUserId}
                    onToggle={(emoji) => onReact?.(message.id, emoji)}
                    align={isOwn ? 'right' : 'left'}
                />
                <Typography variant="caption" component="div" sx={{
                    display: 'flex', alignItems: 'center', mt: 0.2,
                    justifyContent: isOwn ? 'flex-end' : 'flex-start',
                    color: 'text.secondary', fontSize: '0.65rem',
                    px: 1,
                }}>
                    <span>{sending ? t('hardcoded.pages_chatpage.enviando') : formatTime(message.created_at)}</span>
                    {/* Tick de estado SOLO en mensajes propios. Click → Info del mensaje. */}
                    {isOwn && messageStatus && (
                        <MessageStatusTick
                            status={messageStatus}
                            onClick={onShowInfo ? (e) => onShowInfo(e.currentTarget, message) : undefined}
                        />
                    )}
                </Typography>
            </Box>
            {actionBar}
            <ReactionPicker
                open={pickerOpen}
                anchorEl={isMobile ? bubbleRef.current : reactBtnRef.current}
                isOwn={isOwn}
                onPick={(emoji) => onReact?.(message.id, emoji)}
                onClose={() => setPickerOpen(false)}
            />
            {/* Menú de acciones táctil (móvil): tap/long-press sobre la burbuja */}
            {isMobile && (
                <Menu
                    open={!!touchMenuAnchor}
                    anchorEl={touchMenuAnchor}
                    onClose={closeTouchMenu}
                    anchorOrigin={{ vertical: 'top', horizontal: isOwn ? 'right' : 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: isOwn ? 'right' : 'left' }}
                    MenuListProps={{ dense: false }}
                    PaperProps={{ sx: { borderRadius: 2, minWidth: 180 } }}
                >
                    <MenuItem
                        sx={{ minHeight: 44 }}
                        onClick={() => { closeTouchMenu(); setPickerOpen(true); }}
                    >
                        <ReactIcon sx={{ fontSize: 20, mr: 1.5 }} /> {i18n.t('hardcoded.pages_chatpage.reaccionar')}</MenuItem>
                    <MenuItem
                        sx={{ minHeight: 44 }}
                        onClick={() => { closeTouchMenu(); onReply?.(message); }}
                    >
                        <ReplyIcon sx={{ fontSize: 20, mr: 1.5 }} /> {i18n.t('hardcoded.pages_chatpage.responder')}</MenuItem>
                    {isOwn && onShowInfo && (
                        <MenuItem
                            sx={{ minHeight: 44 }}
                            onClick={() => { const el = touchMenuAnchor; closeTouchMenu(); onShowInfo(el, message); }}
                        >
                            <InfoOutlinedIcon sx={{ fontSize: 20, mr: 1.5 }} /> {t('hardcoded.pages_chatpage.info_del_mensaje')}</MenuItem>
                    )}
                </Menu>
            )}
        </Box>
    );
});

// ============ Indicador "escribiendo…" con puntos animados ============
const TypingIndicator = ({ names = [] }) => {
  const { t } = useTranslation();
    if (!names.length) return null;
    let label;
    if (names.length === 1) label = t('hardcoded.pages_chatpage.names0_esta_escribiendo', { names0: names[0] });
    else if (names.length === 2) label = t('hardcoded.pages_chatpage.names0_y_names1_estan_escribiendo', { names0: names[0], names1: names[1] });
    else label = t('hardcoded.pages_chatpage.varios_estan_escribiendo');
    return (
        <Box
            component={motion.div}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.5, ml: 5 }}
        >
            <Box sx={{ display: 'flex', gap: 0.4 }}>
                {[0, 1, 2].map((i) => (
                    <Box key={i} component={motion.span}
                        animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
                        transition={{ duration: 1, repeat: Infinity, delay: i * 0.18 }}
                        sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'text.secondary' }} />
                ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {label}…
            </Typography>
        </Box>
    );
};

// ============ Página principal ============
const ChatPage = () => {
  const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { showError, showSuccess, showInfo } = useNotification();
    const { currentCompany, user } = useAuth();
    const currentUserId = user?.user_id || user?.id;

    const channelTypeMeta = useMemo(() => getChannelTypeMeta(t), [t]);
    const mentionCategories = useMemo(() => getMentionCategories(t), [t]);

    const [channels, setChannels] = useState([]);
    // Diferenciar cargando / vacío / error al listar canales: un timeout o 500
    // NO debe mostrarse como "no hay canales" ni borrar la lista ya cargada.
    const [channelsLoading, setChannelsLoading] = useState(true);
    const [channelsError, setChannelsError] = useState(false);
    const [unread, setUnread] = useState({}); // {channelId: {count, mentions}}
    const [selectedId, setSelectedId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingMsgs, setLoadingMsgs] = useState(false);
    const [body, setBody] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [directOpen, setDirectOpen] = useState(false);
    // Filtro de la lista lateral: con muchas conversaciones, buscar es más rápido
    // que recorrer la lista con la rueda del ratón.
    const [channelFilter, setChannelFilter] = useState('');
    const inputRef = useRef(null);
    const messagesScrollRef = useRef(null);

    // Baja SOLO el panel de mensajes. Nunca `scrollIntoView`: ese método mueve
    // también a los contenedores con scroll de arriba y sacaría la cabecera de
    // la pantalla al entrar al chat.
    const scrollMessagesToBottom = useCallback((behavior = 'auto') => {
        const el = messagesScrollRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior });
    }, []);

    const fileInputRef = useRef(null);

    const [mentionAnchor, setMentionAnchor] = useState(null);
    const [mentionOpen, setMentionOpen] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');
    const [pendingMentions, setPendingMentions] = useState([]);

    const [canDeleteMap, setCanDeleteMap] = useState({});
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Responder/citar: mensaje al que se está respondiendo (o null).
    const [replyTo, setReplyTo] = useState(null);
    // Mensaje resaltado tras hacer scroll a una cita.
    const [highlightId, setHighlightId] = useState(null);
    const highlightTimerRef = useRef(null);
    // "Escribiendo…": {userId: {name, ts}} para el canal activo.
    const [typingUsers, setTypingUsers] = useState({});
    const typingTimersRef = useRef({});
    // Throttle del POST /typing al teclear.
    const lastTypingSentRef = useRef(0);
    // ---------- Recibos estilo WhatsApp (entregado/visto) del canal activo ----------
    // Mapa { user_id -> {user_name, last_delivered_at, last_read_at} } de TODOS los
    // miembros (incluido yo). Para el cálculo de ticks se excluye mi user_id.
    const [receipts, setReceipts] = useState({});
    // Throttle del ACK de entrega (POST /delivered) y memoria del último id ackeado.
    const lastDeliveredAckRef = useRef({ channelId: null, messageId: null, ts: 0 });
    // Popover "Info del mensaje".
    const [infoPopover, setInfoPopover] = useState(null); // { anchorEl, message }
    // Control de autoscroll y botón "ir al final".
    const [atBottom, setAtBottom] = useState(true);
    const atBottomRef = useRef(true);
    const [newSinceScroll, setNewSinceScroll] = useState(0);

    // Adjuntos en cola para enviar
    const [stagedFiles, setStagedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    // Anti doble envío: bloqueo mientras hay un envío en curso.
    const [sending, setSending] = useState(false);
    const sendingRef = useRef(false); // guardia síncrona contra doble click rápido
    // Reutiliza el MISMO client_message_id si el usuario reintenta tras un fallo.
    const pendingClientMsgIdRef = useRef(null);
    const [uploadPct, setUploadPct] = useState(0);
    const [dragOver, setDragOver] = useState(false);
    const [recordingActive, setRecordingActive] = useState(false);
    const [videoNoteOpen, setVideoNoteOpen] = useState(false);

    // Llamadas
    const [activeCall, setActiveCall] = useState(null);       // overlay abierto {call, iceServers, ringTimeout}
    const [channelCall, setChannelCall] = useState(null);     // llamada activa del canal (banner)
    const [incomingInvite, setIncomingInvite] = useState(null);
    const inviteTimerRef = useRef(null);

    // Sonidos / preferencias
    const [soundState, setSoundState] = useState(soundManager.getState());
    const [soundBannerDismissed, setSoundBannerDismissed] = useState(
        () => localStorage.getItem('chatSoundBannerDismissed') === '1'
    );
    const [prefsOpen, setPrefsOpen] = useState(false);
    const [, setPrefsVersion] = useState(0); // re-render al cambiar prefs (mute por canal)

    // Estado del permiso de Web Push para mostrar (o no) el banner de activación.
    const [pushPerm, setPushPerm] = useState(() => pushPermissionState());
    // Banner de push descartable por sesión (no intrusivo).
    const [pushBannerDismissed, setPushBannerDismissed] = useState(false);

    const getUnread = (chId) => {
        const u = unread[chId];
        if (u == null) return { count: 0, mentions: 0 };
        if (typeof u === 'number') return { count: u, mentions: 0 };
        return { count: u.count || 0, mentions: u.mentions || 0 };
    };

    const loadChannels = async () => {
        try {
            const [chRes, uRes] = await Promise.all([api.chat.listChannels(), api.chat.unread()]);
            setChannels(chRes.data || []);
            setUnread(uRes.data || {});
            setChannelsError(false);
            // En móvil arrancamos mostrando la LISTA (no autoseleccionar, taparía la
            // lista). En desktop sí autoseleccionamos el primer canal por comodidad.
            if (!isMobile && !selectedId && chRes.data?.length) setSelectedId(chRes.data[0].id);
        } catch (e) {
            // Fallo transitorio (timeout/red/500): marcar error pero NO borrar los
            // canales ya cargados para no degradar la vista existente.
            setChannelsError(true);
            showError(t('hardcoded.pages_chatpage.no_se_pudieron_cargar_los'));
        } finally {
            setChannelsLoading(false);
        }
    };

    const loadMessages = async (channelId) => {
        if (!channelId) return;
        setLoadingMsgs(true);
        try {
            const r = await api.chat.listMessages(channelId, { limit: 100 });
            const list = r.data || [];
            setMessages(list);
            const last = list[list.length - 1];
            if (last) api.chat.markRead(channelId, last.id).catch(() => {});
            setUnread((p) => ({ ...p, [channelId]: { count: 0, mentions: 0 } }));
            // ACK de entrega por los mensajes de otros ya cargados.
            maybeSendDeliveredAck(channelId, list);
        } catch (e) {
            showError(t('hardcoded.pages_chatpage.error_cargando_mensajes'));
        } finally { setLoadingMsgs(false); }
    };

    // ---------- Recibos (entregado/visto) ----------
    // Carga los recibos del canal y los normaliza a { user_id -> {...} }.
    const fetchReceipts = useCallback(async (channelId) => {
        if (!channelId) return;
        try {
            const r = await api.chat.getReceipts(channelId);
            const rows = r.data?.receipts || [];
            const map = {};
            rows.forEach((row) => {
                if (row && row.user_id != null) {
                    map[String(row.user_id)] = {
                        user_name: row.user_name,
                        last_delivered_at: row.last_delivered_at || null,
                        last_read_at: row.last_read_at || null,
                    };
                }
            });
            setReceipts(map);
        } catch (_) { /* recibos opcionales: no bloquean el chat */ }
    }, []);

    // Mezcla un evento WS de recibo (chat_delivered/chat_read) con el estado actual,
    // tomando el máximo entre el valor actual y el del evento (nunca retrocede).
    const applyReceiptEvent = useCallback((userId, field, atIso) => {
        if (userId == null || !atIso) return;
        const uid = String(userId);
        setReceipts((prev) => {
            const cur = prev[uid] || {};
            if (ts(cur[field]) >= ts(atIso)) return prev; // no avanza → sin re-render
            return { ...prev, [uid]: { ...cur, [field]: atIso } };
        });
    }, []);

    // Envía ACK de ENTREGA (POST /delivered) por el último mensaje de OTROS, con
    // throttle ~1.5s y solo si avanzó. NUNCA por mensajes propios.
    const maybeSendDeliveredAck = useCallback((channelId, msgs) => {
        if (!channelId || !Array.isArray(msgs) || !msgs.length) return;
        // Último mensaje de OTRO con id real del servidor.
        let lastOther = null;
        for (let i = msgs.length - 1; i >= 0; i -= 1) {
            const m = msgs[i];
            if (!m.__sending && m.id && String(m.user_id) !== String(currentUserId)) { lastOther = m; break; }
        }
        if (!lastOther) return;
        const ack = lastDeliveredAckRef.current;
        const now = Date.now();
        // Mismo canal + mismo último id ya ackeado → nada que hacer.
        if (ack.channelId === channelId && String(ack.messageId) === String(lastOther.id)) return;
        // Throttle 1.5s (salvo cambio de canal, donde ackeamos de inmediato).
        if (ack.channelId === channelId && now - ack.ts < 1500) return;
        lastDeliveredAckRef.current = { channelId, messageId: lastOther.id, ts: now };
        api.chat.markDelivered(channelId, lastOther.id).catch(() => {});
    }, [currentUserId]);

    // ---------- Reacciones (optimista + reconciliación) ----------
    const toggleReaction = useCallback(async (messageId, emoji) => {
        const uid = String(currentUserId);
        // Optimismo: alterna localmente al instante.
        setMessages((prev) => prev.map((m) => {
            if (String(m.id) !== String(messageId)) return m;
            const reactions = Array.isArray(m.reactions) ? m.reactions.map((r) => ({ ...r, user_ids: [...(r.user_ids || [])] })) : [];
            const idx = reactions.findIndex((r) => r.emoji === emoji);
            if (idx === -1) {
                reactions.push({ emoji, count: 1, user_ids: [uid] });
            } else {
                const r = reactions[idx];
                const has = (r.user_ids || []).some((x) => String(x) === uid);
                if (has) {
                    r.user_ids = r.user_ids.filter((x) => String(x) !== uid);
                    r.count = Math.max(0, (r.count || 1) - 1);
                    if (r.count === 0) reactions.splice(idx, 1);
                } else {
                    r.user_ids = [...(r.user_ids || []), uid];
                    r.count = (r.count || 0) + 1;
                }
            }
            return { ...m, reactions };
        }));
        try {
            const r = await api.chat.toggleReaction(messageId, emoji);
            // Reconcilia con la verdad del servidor.
            const serverReactions = r.data?.reactions;
            if (Array.isArray(serverReactions)) {
                setMessages((prev) => prev.map((m) =>
                    String(m.id) === String(messageId) ? { ...m, reactions: serverReactions } : m));
            }
        } catch (e) {
            showError(t('hardcoded.pages_chatpage.no_se_pudo_registrar_la'));
            // Revierte recargando reacciones reales si es posible (best-effort: deja WS reconciliar).
        }
    }, [currentUserId, showError]);

    // ---------- Saltar a un mensaje citado + resaltarlo ----------
    const jumpToMessage = useCallback((messageId) => {
        const el = document.getElementById(`msg-${messageId}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightId(messageId);
        clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = setTimeout(() => setHighlightId(null), 1800);
    }, []);

    const startReply = useCallback((message) => {
        setReplyTo(message);
        setTimeout(() => inputRef.current?.focus(), 0);
    }, []);

    // Deep link: /chat?channel=...&call=...
    useEffect(() => {
        const sp = new URLSearchParams(location.search);
        const chId = sp.get('channel');
        if (chId) setSelectedId(chId);
    }, [location.search]);

    useEffect(() => {
        loadChannels();
        // Activar Web Push para este navegador (pide permiso si hace falta) y
        // refrescar el estado del permiso para decidir si mostramos el banner.
        ensurePushSubscription().finally(() => setPushPerm(pushPermissionState()));
        // Conectar deep links de notificaciones (push-navigate desde el SW) con
        // la navegación SPA, para navegadores donde el SW no puede usar navigate().
        const offNav = initPushNavigation((url) => navigate(url));
        // Cargar preferencias de notificación (sonidos/push/mute por canal)
        chatPrefs.load();
        const offPrefs = chatPrefs.onChange(() => setPrefsVersion((v) => v + 1));
        const offSound = soundManager.onStateChange(setSoundState);
        return () => { offNav?.(); offPrefs?.(); offSound?.(); };
    }, []); // eslint-disable-line

    useEffect(() => {
        if (!selectedId || selectedId === AI_CHANNEL_ID) return;  // el canal IA no usa la API de chat
        loadMessages(selectedId);
        setStagedFiles([]);
        setReplyTo(null);
        setTypingUsers({});
        Object.values(typingTimersRef.current).forEach((t) => clearTimeout(t));
        typingTimersRef.current = {};
        setNewSinceScroll(0);
        atBottomRef.current = true;
        setAtBottom(true);
        // Reinicia recibos y ACK al cambiar de canal, luego carga los del canal nuevo.
        setReceipts({});
        setInfoPopover(null);
        lastDeliveredAckRef.current = { channelId: null, messageId: null, ts: 0 };
        fetchReceipts(selectedId);
        if (!(selectedId in canDeleteMap)) {
            api.chat.canDelete(selectedId)
                .then((r) => setCanDeleteMap((p) => ({ ...p, [selectedId]: !!r.data?.can_delete })))
                .catch(() => setCanDeleteMap((p) => ({ ...p, [selectedId]: false })));
        }
        // ¿Hay llamada activa en este canal?
        api.chat.activeCall(selectedId)
            .then((r) => setChannelCall(r.data?.call || null))
            .catch(() => setChannelCall(null));
    }, [selectedId]); // eslint-disable-line

    const selectedIdRef = useRef(selectedId);
    useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
    const activeCallRef = useRef(activeCall);
    useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);

    // WebSocket: mensajes en vivo + menciones + llamadas + sync multi-pestaña
    useEffect(() => {
        try {
            if (!websocketService.isConnected) {
                const token = localStorage.getItem('authToken') || localStorage.getItem('access_token');
                if (token) websocketService.connect(token);
            }
        } catch (_) { /* noop */ }

        const handler = (data) => {
            if (!data || !data.channel_id) return;
            const currentSel = selectedIdRef.current;
            const isOwnMsg = String(data.user_id) === String(currentUserId);
            const isMention = (data.mentions || []).some((m) => m.type === 'user' && String(m.id) === String(currentUserId));

            // 🔊 Sonido según reglas (propio: nunca; canal activo+foco: solo mención;
            // canal silenciado/solo-menciones/DND/horario silencioso: respetado por chatPrefs)
            const soundKind = chatPrefs.shouldPlayMessageSound({
                channelId: data.channel_id,
                isOwn: isOwnMsg,
                isMention,
                isActiveChannel: data.channel_id === currentSel,
                windowFocused: typeof document !== 'undefined' && document.hasFocus(),
            });
            if (soundKind) soundManager.play(soundKind, { eventId: `msg-${data.id}` });

            if (data.channel_id === currentSel) {
                // Dedupe por id Y por client_message_id (evita duplicar el optimista/HTTP).
                setMessages((p) => {
                    if (p.some((m) =>
                        String(m.id) === String(data.id)
                        || (data.client_message_id && String(m.client_message_id) === String(data.client_message_id))
                    )) return p;
                    return [...p, { ...data, __entering: true }];
                });
                // Si llega de otro y no estás abajo, contabiliza no leídos para el badge.
                if (!isOwnMsg && !atBottomRef.current) {
                    setNewSinceScroll((n) => n + 1);
                }
                // Limpia el indicador de escritura de quien acaba de enviar.
                setTypingUsers((prev) => {
                    if (!prev[data.user_id]) return prev;
                    const n = { ...prev }; delete n[data.user_id]; return n;
                });
                api.chat.markRead(currentSel, data.id).catch(() => {});
                setUnread((p) => ({ ...p, [data.channel_id]: { count: 0, mentions: 0 } }));
                // ACK de ENTREGA: solo si el mensaje recibido es de OTRO.
                if (!isOwnMsg) maybeSendDeliveredAck(currentSel, [data]);
            } else if (!isOwnMsg) {
                setUnread((p) => {
                    const prev = p[data.channel_id];
                    const base = (prev && typeof prev === 'object') ? prev : { count: prev || 0, mentions: 0 };
                    return { ...p, [data.channel_id]: { ...base, count: (base.count || 0) + 1, mentions: (base.mentions || 0) + (isMention ? 1 : 0) } };
                });
                // Toast no intrusivo de mensaje en otro canal (si no está silenciado)
                const chName = (channels.find((c) => c.id === data.channel_id) || {}).name;
                if (chName && !chatPrefs.isChannelMuted(data.channel_id)) {
                    showInfo(`💬 ${data.user_name || t('hardcoded.pages_chatpage.mensaje_nuevo')} en #${chName}`);
                }
            }
            setChannels((p) => {
                const exists = p.some((c) => c.id === data.channel_id);
                if (!exists) { loadChannels(); return p; }
                return p.map((c) => c.id === data.channel_id
                    ? { ...c, last_message_at: data.created_at || new Date().toISOString() }
                    : c);
            });
        };

        const off = websocketService.on('chat_message', handler);

        const offDel = websocketService.on('chat_channel_deleted', (data) => {
            const id = data?.channel_id;
            if (!id) return;
            setChannels((p) => p.filter((c) => c.id !== id));
            setUnread((p) => { const n = { ...p }; delete n[id]; return n; });
            setCanDeleteMap((p) => { const n = { ...p }; delete n[id]; return n; });
            if (selectedIdRef.current === id) {
                setSelectedId(null);
                setMessages([]);
            }
        });

        const offUpd = websocketService.on('chat_channel_updated', () => loadChannels());

        // Sincronización de leídos entre pestañas del mismo usuario
        const offUnread = websocketService.on('chat_unread_updated', (data) => {
            if (!data?.channel_id) return;
            setUnread((p) => ({ ...p, [data.channel_id]: { count: data.count || 0, mentions: data.mentions || 0 } }));
        });

        // Mención directa → aviso destacado
        const offMention = websocketService.on('chat_mention', (data) => {
            if (!data) return;
            if (data.channel_id !== selectedIdRef.current) {
                showInfo(t('hardcoded.pages_chatpage.databy_user_nam_te_menciono_en_datachannel_nam', { databy_user_nam: data.by_user_name || 'Alguien', datachannel_nam: data.channel_name || t('hardcoded.pages_chatpage.un_canal') }));
            }
        });

        // Reacciones en vivo: actualiza message.reactions del mensaje afectado.
        const offReaction = websocketService.on('chat_reaction', (data) => {
            if (!data || data.channel_id !== selectedIdRef.current) return;
            if (!Array.isArray(data.reactions)) return;
            setMessages((p) => p.map((m) =>
                String(m.id) === String(data.message_id) ? { ...m, reactions: data.reactions } : m));
        });

        // Recibos en vivo: ENTREGADO. {channel_id, user_id, last_message_id, at}
        const offDelivered = websocketService.on('chat_delivered', (data) => {
            if (!data || data.channel_id !== selectedIdRef.current) return;
            applyReceiptEvent(data.user_id, 'last_delivered_at', data.at);
        });
        // Recibos en vivo: VISTO/LEÍDO. {channel_id, user_id, last_message_id, at}
        const offRead = websocketService.on('chat_read', (data) => {
            if (!data || data.channel_id !== selectedIdRef.current) return;
            // Leer implica también entregado: avanza ambos campos.
            applyReceiptEvent(data.user_id, 'last_read_at', data.at);
            applyReceiptEvent(data.user_id, 'last_delivered_at', data.at);
        });

        // "Escribiendo…": ignora el propio user_id; auto-oculta tras 4s.
        const offTyping = websocketService.on('chat_typing', (data) => {
            if (!data || data.channel_id !== selectedIdRef.current) return;
            if (String(data.user_id) === String(currentUserId)) return;
            const uid = String(data.user_id);
            setTypingUsers((prev) => ({ ...prev, [uid]: { name: data.user_name || t('hardcoded.pages_chatpage.alguien'), ts: Date.now() } }));
            clearTimeout(typingTimersRef.current[uid]);
            typingTimersRef.current[uid] = setTimeout(() => {
                setTypingUsers((prev) => { const n = { ...prev }; delete n[uid]; return n; });
            }, 4000);
        });

        // Transcodificación lista: el backend terminó de convertir un adjunto de
        // audio/video. Actualizamos el adjunto correspondiente en el estado para
        // que el componente se re-renderice y ahora SÍ cargue el player con la
        // versión compatible (mime_type efectivo). data: { attachment_id,
        // message_id, channel_id, playback_mime }.
        const offAttachReady = websocketService.on('chat_attachment_ready', (data) => {
            if (!data || data.channel_id !== selectedIdRef.current) return;
            const aId = data.attachment_id;
            const mId = data.message_id;
            if (aId == null) return;
            setMessages((p) => p.map((m) => {
                if (mId != null && String(m.id) !== String(mId)) return m;
                const atts = m.attachments;
                if (!Array.isArray(atts) || !atts.some((a) => String(a.id) === String(aId))) return m;
                return {
                    ...m,
                    attachments: atts.map((a) => (String(a.id) === String(aId)
                        ? { ...a, transcode_status: t('hardcoded.pages_chatpage.done'), mime_type: data.playback_mime || a.mime_type }
                        : a)),
                };
            }));
        });

        // Llamadas
        const offCallStart = websocketService.on('chat_call_started', (data) => {
            if (!data) return;
            // Banner si es el canal abierto
            if (data.channel_id === selectedIdRef.current) setChannelCall(data);
            // Invitación entrante (no para quien la inició) — con tono y expiración
            if (String(data.started_by) !== String(currentUserId) && !activeCallRef.current) {
                setIncomingInvite(data);
                clearTimeout(inviteTimerRef.current);
                const timeoutS = Math.max(10, data.ring_timeout || 45);
                inviteTimerRef.current = setTimeout(() => {
                    setIncomingInvite((inv) => {
                        if (inv && String(inv.id) === String(data.id)) {
                            soundManager.stopRing(String(data.id));
                            showInfo(t('hardcoded.pages_chatpage.llamada_perdida_de_databy_user_nam_en', { databy_user_nam: data.by_user_name || t('audit.table.user'), datachannel_nam: data.channel_name || 'canal' }));
                            return null;
                        }
                        return inv;
                    });
                }, timeoutS * 1000);
            }
        });
        const offCallEnd = websocketService.on('chat_call_ended', (data) => {
            if (data?.channel_id === selectedIdRef.current) setChannelCall(null);
            setIncomingInvite((inv) => {
                if (inv && String(inv.id) === String(data?.call_id)) {
                    // El emisor canceló o expiró antes de contestar → perdida
                    soundManager.stopRing(String(inv.id));
                    clearTimeout(inviteTimerRef.current);
                    showInfo(t('hardcoded.pages_chatpage.llamada_perdida_de_invby_user_name', { invby_user_name: inv.by_user_name || t('audit.table.user') }));
                    return null;
                }
                return inv;
            });
        });
        const offCallRejected = websocketService.on('chat_call_rejected', (data) => {
            // Aviso para quien inició (el overlay también muestra el estado)
            if (data && String(data.user_id) !== String(currentUserId) && activeCallRef.current
                && String(activeCallRef.current.call?.id) === String(data.call_id)) {
                showInfo(t('hardcoded.pages_chatpage.datauser_nameta_rechazo_la_llamada', { datauser_nameta: data.user_name || t('audit.table.user') }));
            }
        });

        return () => { off?.(); offDel?.(); offUpd?.(); offUnread?.(); offMention?.(); offReaction?.(); offTyping?.(); offDelivered?.(); offRead?.(); offAttachReady?.(); offCallStart?.(); offCallEnd?.(); offCallRejected?.(); clearTimeout(inviteTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channels.length]);

    const handleDeleteChannel = async () => {
        if (!deleteConfirm) return;
        try {
            await api.chat.deleteChannel(deleteConfirm.id);
            const removedId = deleteConfirm.id;
            setDeleteConfirm(null);
            setChannels((p) => p.filter((c) => c.id !== removedId));
            setCanDeleteMap((p) => { const n = { ...p }; delete n[removedId]; return n; });
            if (selectedId === removedId) {
                setSelectedId(null);
                setMessages([]);
            }
        } catch (e) {
            showError(e?.response?.data?.detail || t('hardcoded.pages_chatpage.no_se_pudo_eliminar_el'));
        }
    };

    // Autoscroll SOLO si ya estabas abajo (no interrumpe lectura de historial).
    useEffect(() => {
        if (atBottomRef.current) {
            scrollMessagesToBottom('smooth');
        }
        // Limpia la marca de animación de entrada tras el primer render.
        if (messages.some((m) => m.__entering)) {
            const t = setTimeout(() => {
                setMessages((p) => p.map((m) => (m.__entering ? { ...m, __entering: false } : m)));
            }, 250);
            return () => clearTimeout(t);
        }
    }, [messages, scrollMessagesToBottom]);

    // Limpieza de timers al desmontar.
    useEffect(() => () => {
        clearTimeout(highlightTimerRef.current);
        Object.values(typingTimersRef.current).forEach((t) => clearTimeout(t));
    }, []);

    // Rastrea si el usuario está cerca del fondo del scroll.
    const handleScroll = useCallback(() => {
        const el = messagesScrollRef.current;
        if (!el) return;
        const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        atBottomRef.current = near;
        setAtBottom(near);
        if (near) setNewSinceScroll(0);
    }, []);

    const scrollToBottom = useCallback(() => {
        atBottomRef.current = true;
        setAtBottom(true);
        setNewSinceScroll(0);
        scrollMessagesToBottom('smooth');
    }, [scrollMessagesToBottom]);

    // Emite "escribiendo…" con throttle (~2.5s) mientras se teclea.
    const emitTyping = useCallback(() => {
        const ch = selectedIdRef.current;
        if (!ch) return;
        const now = Date.now();
        if (now - lastTypingSentRef.current < 2500) return;
        lastTypingSentRef.current = now;
        api.chat.sendTyping(ch).catch(() => {});
    }, []);

    const handleInput = (e) => {
        const val = e.target.value;
        setBody(val);
        if (val.trim()) emitTyping();
        const sel = e.target.selectionStart;
        const upTo = val.slice(0, sel);
        const m = upTo.match(/(?:^|\s)@([\p{L}\p{N}._\-]*)$/u);
        if (m !== null) {
            setMentionQuery(m[1] || '');
            setMentionAnchor(inputRef.current || e.target);
            setMentionOpen(true);
        } else {
            setMentionOpen(false);
            setMentionQuery('');
        }
    };

    const insertMention = (item) => {
        const el = inputRef.current;
        if (!el) return;
        const sel = el.selectionStart ?? body.length;
        const before = body.slice(0, sel).replace(/(?:^|\s)@([\p{L}\p{N}._\-]*)$/u, (match) => match.startsWith(' ') ? ' ' : '');
        const after = body.slice(sel);
        const mentionText = `@${item.label}`;
        const newBody = `${before}${mentionText} ${after}`;
        setBody(newBody);
        setMentionOpen(false);
        setMentionQuery('');
        setPendingMentions((p) => [...p, {
            type: item.type, id: item.id, label: item.label,
            start: before.length, end: before.length + mentionText.length,
        }]);
        setTimeout(() => {
            el.focus();
            const cursor = before.length + mentionText.length + 1;
            try { el.setSelectionRange(cursor, cursor); } catch { /* noop */ }
        }, 0);
    };

    // ---------- Adjuntos ----------
    const MAX_FILES = 10;
    const addFiles = (fileList) => {
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;
        setStagedFiles((prev) => {
            const merged = [...prev, ...incoming].slice(0, MAX_FILES);
            if (prev.length + incoming.length > MAX_FILES) showError(t('hardcoded.pages_chatpage.maximo_max_files_archivos_por_mensaje', { MAX_FILES: MAX_FILES }));
            return merged;
        });
    };

    const sendVoice = async (file, durationSeconds, { waveform = null } = {}) => {
        if (!selectedId) return;
        if (sendingRef.current) return; // anti doble envío también para notas de voz
        sendingRef.current = true;
        setSending(true);
        setUploading(true); setUploadPct(0);
        const clientMessageId = genClientMessageId();
        try {
            const r = await api.chat.postAttachments(selectedId, [file], {
                isVoice: true, durationSeconds, clientMessageId,
                waveform, attachmentKind: 'voice',
                onProgress: (e) => setUploadPct(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
            });
            setMessages((p) => p.some((m) =>
                String(m.id) === String(r.data.id)
                || (r.data.client_message_id && String(m.client_message_id) === String(r.data.client_message_id))
            ) ? p : [...p, r.data]);
        } catch (e) {
            showError(e?.response?.data?.detail || t('hardcoded.pages_chatpage.no_se_pudo_enviar_la'));
        } finally {
            setUploading(false);
            sendingRef.current = false;
            setSending(false);
        }
    };

    const sendVideoNote = async (file, { durationSeconds = null, width = null, height = null } = {}) => {
        if (!selectedId) return;
        if (sendingRef.current) return;
        sendingRef.current = true;
        setSending(true);
        setUploading(true); setUploadPct(0);
        const clientMessageId = genClientMessageId();
        try {
            const r = await api.chat.postAttachments(selectedId, [file], {
                durationSeconds, width, height, attachmentKind: 'video_note', clientMessageId,
                onProgress: (e) => setUploadPct(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
            });
            setMessages((p) => p.some((m) =>
                String(m.id) === String(r.data.id)
                || (r.data.client_message_id && String(m.client_message_id) === String(r.data.client_message_id))
            ) ? p : [...p, r.data]);
        } catch (e) {
            showError(e?.response?.data?.detail || t('hardcoded.pages_chatpage.no_se_pudo_enviar_la'));
        } finally {
            setUploading(false);
            sendingRef.current = false;
            setSending(false);
        }
    };

    const sendMessage = async () => {
        const text = body.trim();
        if ((!text && !stagedFiles.length) || !selectedId) return;
        // Guardia anti doble envío: ignora clicks/Enter adicionales si ya hay uno en curso.
        if (sendingRef.current) return;
        sendingRef.current = true;
        setSending(true);

        const SUPPORTED = new Set(['user', t('hardcoded.pages_chatpage.operator'), t('hardcoded.pages_chatanalyticspage.company'), t('hardcoded.components_ai_aientitycards.vehicle'), t('hardcoded.components_ai_aientitycards.ticket'), t('hardcoded.components_ai_aientitycards.task'), t('hardcoded.components_schematics_vehicleticketsdrawer.sensor'), 'receiver', t('hardcoded.pages_chatpage.profile')]);
        const filteredMentions = pendingMentions
            .filter((m) => body.includes(`@${m.label}`))
            .filter((m) => SUPPORTED.has(m.type));

        // Reutiliza el MISMO id si este es un reintento tras un fallo previo;
        // genera uno nuevo solo para un envío nuevo. El backend deduplica por él.
        if (!pendingClientMsgIdRef.current) pendingClientMsgIdRef.current = genClientMessageId();
        const clientMessageId = pendingClientMsgIdRef.current;

        const replyToMessageId = replyTo?.id || null;
        try {
            let r;
            if (stagedFiles.length) {
                setUploading(true); setUploadPct(0);
                r = await api.chat.postAttachments(selectedId, stagedFiles, {
                    body: text,
                    clientMessageId,
                    replyToMessageId,
                    onProgress: (e) => setUploadPct(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
                });
            } else {
                r = await api.chat.postMessage(selectedId, text, filteredMentions, [], clientMessageId, replyToMessageId);
            }
            // Reconcilia por id Y por client_message_id para no duplicar con lo que llegue por WS.
            setMessages((p) => p.some((m) =>
                String(m.id) === String(r.data.id)
                || (r.data.client_message_id && String(m.client_message_id) === String(r.data.client_message_id))
            ) ? p : [...p, r.data]);
            // Solo limpiamos el composer tras confirmar éxito.
            setBody('');
            setPendingMentions([]);
            setStagedFiles([]);
            setReplyTo(null);
            pendingClientMsgIdRef.current = null;
            // Tras enviar, siempre baja al final.
            atBottomRef.current = true; setAtBottom(true); setNewSinceScroll(0);
        } catch (e) {
            // Conserva contenido y el MISMO client_message_id para permitir reintentar.
            showError(e?.response?.data?.detail || t('hardcoded.pages_chatpage.mensaje_no_enviado_reintenta'));
        } finally {
            setUploading(false);
            sendingRef.current = false;
            setSending(false);
        }
    };

    // ---------- Llamadas ----------
    const userNamesById = useMemo(() => {
        const map = {};
        messages.forEach((m) => { if (m.user_id) map[m.user_id] = m.user_name || m.user_email; });
        (channelCall?.participants || []).forEach((p) => { if (p.user_id) map[p.user_id] = map[p.user_id] || p.user_name; });
        return map;
    }, [messages, channelCall]);

    const startCall = async (type) => {
        if (!selectedId) return;
        soundManager.unlock();
        try {
            const r = await api.chat.startCall(selectedId, type);
            const { call, existing, iceServers, ring_timeout: rt } = r.data;
            if (existing) {
                // Ya hay llamada → unirse
                const j = await api.chat.joinCall(call.id);
                setActiveCall({ call: j.data.call, iceServers: j.data.iceServers, ringTimeout: rt || 45 });
            } else {
                setActiveCall({ call, iceServers, ringTimeout: rt || 45 });
            }
            setChannelCall(call);
        } catch (e) {
            soundManager.play('error');
            showError(e?.response?.data?.detail || t('hardcoded.pages_chatpage.no_se_pudo_iniciar_la'));
        }
    };

    const joinChannelCall = async () => {
        if (!channelCall) return;
        soundManager.unlock();
        try {
            const j = await api.chat.joinCall(channelCall.id);
            setActiveCall({ call: j.data.call, iceServers: j.data.iceServers });
        } catch (e) {
            soundManager.play('error');
            showError(e?.response?.data?.detail || t('hardcoded.pages_chatpage.no_se_pudo_unir_a'));
        }
    };

    const acceptIncoming = async () => {
        const inv = incomingInvite;
        setIncomingInvite(null);
        clearTimeout(inviteTimerRef.current);
        if (!inv) return;
        soundManager.stopRing(String(inv.id));
        try {
            setSelectedId(inv.channel_id);
            const j = await api.chat.joinCall(inv.id);
            setActiveCall({ call: j.data.call, iceServers: j.data.iceServers });
        } catch (e) {
            soundManager.play('error');
            showError(t('hardcoded.pages_chatpage.la_llamada_ya_no_esta'));
        }
    };

    const rejectIncoming = async () => {
        const inv = incomingInvite;
        setIncomingInvite(null);
        clearTimeout(inviteTimerRef.current);
        if (!inv) return;
        soundManager.stopRing(String(inv.id));
        try { await api.chat.rejectCall(inv.id); } catch { /* noop */ }
    };

    // Acciones desde el push de llamada (?call=..&action=answer|reject)
    useEffect(() => {
        const sp = new URLSearchParams(location.search);
        const callId = sp.get('call');
        const action = sp.get('action');
        if (!callId || !action) return;
        (async () => {
            try {
                if (action === 'answer') {
                    const j = await api.chat.joinCall(callId);
                    setActiveCall({ call: j.data.call, iceServers: j.data.iceServers });
                } else if (action === 'reject') {
                    await api.chat.rejectCall(callId);
                    showInfo(t('hardcoded.pages_chatpage.llamada_rechazada'));
                }
            } catch {
                showInfo(t('hardcoded.pages_chatpage.la_llamada_ya_no_esta'));
            }
            // limpiar la URL para no repetir la acción
            navigate('/chat' + (sp.get('channel') ? `?channel=${sp.get('channel')}` : ''), { replace: true });
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const selectedChannel = useMemo(() => channels.find((c) => c.id === selectedId), [channels, selectedId]);

    const messagesByDay = useMemo(() => {
        const out = [];
        let currentDay = null;
        let prevSender = null;
        let prevTime = 0;
        for (const m of messages) {
            const day = formatDay(m.created_at);
            if (day !== currentDay) {
                out.push({ type: 'separator', label: day, key: `sep-${day}-${m.id}` });
                currentDay = day;
                prevSender = null;
                prevTime = 0;
            }
            const t = new Date(m.created_at).getTime();
            const sameSender = prevSender === m.user_id;
            const closeInTime = t - prevTime < 60 * 1000;
            const showAvatar = !sameSender || !closeInTime;
            const showName = showAvatar;
            out.push({ type: 'msg', message: m, showAvatar, showName, key: m.id });
            prevSender = m.user_id;
            prevTime = t;
        }
        return out;
    }, [messages]);

    const sortedChannels = useMemo(() => {
        return [...channels].sort((a, b) => {
            const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
            const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
            return tb - ta;
        });
    }, [channels]);

    // Directos y canales son cosas distintas y se buscan de forma distinta:
    // a una persona por su nombre, a un canal por su tema. Van en dos grupos.
    const channelGroups = useMemo(() => {
        const termino = channelFilter.trim().toLowerCase();
        const visibles = termino
            ? sortedChannels.filter((c) => channelTitle(c, currentUserId).toLowerCase().includes(termino))
            : sortedChannels;
        return {
            directos: visibles.filter((c) => c.channel_type === 'direct'),
            canales: visibles.filter((c) => c.channel_type !== 'direct'),
            hayFiltro: !!termino,
            totalVisible: visibles.length,
        };
    }, [sortedChannels, channelFilter, currentUserId]);

    const selMeta = selectedChannel ? (channelTypeMeta[selectedChannel.channel_type] || channelTypeMeta.custom) : null;

    const typingList = useMemo(
        () => Object.values(typingUsers).map((u) => u.name),
        [typingUsers],
    );

    // ---------- Recibos: ids de los OTROS miembros (excluido yo) ----------
    const otherMemberIds = useMemo(
        () => Object.keys(receipts).filter((uid) => String(uid) !== String(currentUserId)),
        [receipts, currentUserId],
    );

    // ---------- Mapa { messageId -> 'sending'|'sent'|'delivered'|'seen' } ----------
    // Memoizado para no recalcular por burbuja: solo cambia con mensajes/recibos.
    const messageStatusMap = useMemo(() => {
        const map = {};
        for (const m of messages) {
            if (String(m.user_id) !== String(currentUserId)) continue; // solo míos
            map[m.id || m.client_message_id] = computeMessageStatus(m, receipts, otherMemberIds);
        }
        return map;
    }, [messages, receipts, otherMemberIds, currentUserId]);

    // Abre el popover "Info del mensaje".
    const handleShowInfo = useCallback((anchorEl, message) => {
        setInfoPopover({ anchorEl, message });
    }, []);

    return (
        <Box sx={{
            height: '100%', minHeight: 0, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
        }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}
                sx={{ mb: 2, minWidth: 0, flexShrink: 0 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} color="primary.main">{i18n.t('hardcoded.components_common_sidebar.chat')}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>{t('hardcoded.pages_chatpage.mensajeria_en_tiempo_real_con')}</Typography>
                </Box>
                <Stack direction="row" spacing={{ xs: 0.25, sm: 1 }} sx={{ flexShrink: 0 }}>
                    <Tooltip title={t('hardcoded.pages_chatpage.preferencias_de_notificaciones_y_sonidos')}>
                        <IconButton onClick={() => setPrefsOpen(true)}><ProfileIcon /></IconButton>
                    </Tooltip>
                    <IconButton onClick={loadChannels}><RefreshIcon /></IconButton>
                    {/* En móvil los botones se reducen a iconos para no desbordar el header */}
                    <Tooltip title={t('hardcoded.pages_chatpage.mensaje_directo')}>
                        <IconButton color="primary" onClick={() => setDirectOpen(true)}><UserIcon /></IconButton>
                    </Tooltip>
                    {isMobile ? (
                        <Tooltip title={t('hardcoded.pages_chatpage.nuevo_canal')}>
                            <IconButton color="primary" onClick={() => setCreateOpen(true)}><AddIcon /></IconButton>
                        </Tooltip>
                    ) : (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>{i18n.t('hardcoded.pages_chatpage.nuevo_canal')}</Button>
                    )}
                </Stack>
            </Stack>

            {/* Banner discreto: el navegador bloqueó el audio hasta interactuar */}
            {soundState.blocked && !soundState.unlocked && !soundBannerDismissed && (
                <Alert
                    severity="info"
                    sx={{ mb: 1.5, py: 0.25, alignItems: 'center', flexShrink: 0 }}
                    onClose={() => { setSoundBannerDismissed(true); localStorage.setItem('chatSoundBannerDismissed', '1'); }}
                    action={
                        <Button size="small" variant="outlined" onClick={async () => {
                            const ok = await soundManager.unlock();
                            if (ok) { setSoundBannerDismissed(true); localStorage.setItem('chatSoundBannerDismissed', '1'); }
                        }}>
                            {i18n.t('hardcoded.pages_chatpage.activar_sonidos')}</Button>
                    }
                >
                    {t('hardcoded.pages_chatpage.los_sonidos_de_notificacion_estan')}</Alert>
            )}

            {/* Banner Web Push: visible solo si el navegador soporta push, el permiso
                NO es 'granted' y el usuario no lo descartó en esta sesión. */}
            {pushPerm !== 'unsupported' && pushPerm !== 'granted' && !pushBannerDismissed && (
                <Alert
                    severity={pushPerm === 'denied' ? 'warning' : 'info'}
                    sx={{ mb: 1.5, py: 0.25, alignItems: 'center', flexShrink: 0 }}
                    onClose={() => setPushBannerDismissed(true)}
                    action={pushPerm === 'default' ? (
                        <Button size="small" variant="outlined" onClick={async () => {
                            const perm = await requestPushPermissionExplicitly();
                            setPushPerm(perm);
                            if (perm === 'granted') setPushBannerDismissed(true);
                        }}>
                            {i18n.t('hardcoded.pages_chatpage.activar')}</Button>
                    ) : undefined}
                >
                    {pushPerm === 'denied'
                        ? t('hardcoded.pages_chatpage.notificaciones_bloqueadas')
                        : t('hardcoded.pages_chatpage.activa_las_notificaciones_para_recibir')}
                </Alert>
            )}

            <Box sx={{
                display: 'grid',
                // Móvil: UN solo panel (lista O conversación). Desktop: dos columnas intactas.
                gridTemplateColumns: isMobile ? '1fr' : '300px 1fr',
                gap: 2, flex: 1, minHeight: 0,
            }}>
                {/* Sidebar canales — en móvil se oculta si hay un canal seleccionado */}
                <Paper sx={{
                    overflow: 'hidden', display: isMobile && selectedId ? 'none' : 'flex',
                    flexDirection: 'column', minWidth: 0, minHeight: 0,
                }}>
                    <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                        <TextField
                            fullWidth size="small" placeholder={t('hardcoded.pages_chatpage.buscar_conversacion')}
                            value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                                endAdornment: channelFilter ? (
                                    <IconButton size="small" onClick={() => setChannelFilter('')}>
                                        <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                ) : null,
                                sx: { borderRadius: 2, bgcolor: 'action.hover', '& fieldset': { border: 'none' } },
                            }}
                        />
                    </Box>
                    <List dense sx={{ flex: 1, overflowY: 'auto' }}>
                        {/* Asistente APS — fijado arriba, nativo dentro del chat */}
                        <ListItemButton
                            selected={selectedId === AI_CHANNEL_ID}
                            onClick={() => setSelectedId(AI_CHANNEL_ID)}
                            sx={{
                                borderRadius: 1, mb: 0.5,
                                background: selectedId === AI_CHANNEL_ID
                                    ? 'linear-gradient(135deg, rgba(211,47,47,0.14) 0%, rgba(183,28,28,0.10) 100%)'
                                    : 'linear-gradient(135deg, rgba(211,47,47,0.06) 0%, rgba(183,28,28,0.02) 100%)',
                                '&:hover': { background: 'rgba(211,47,47,0.12)' },
                            }}
                        >
                            <ListItemAvatar sx={{ minWidth: 40 }}>
                                <Avatar sx={{ width: 30, height: 30, background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)' }}>
                                    <AssistantIcon sx={{ fontSize: 18 }} />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2" noWrap sx={{ fontWeight: 800, flex: 1 }}>{i18n.t('hardcoded.components_ai_assistantlauncher.asistente_aps')}</Typography>
                                        <Chip size="small" label="IA" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, bgcolor: 'primary.main', color: '#fff' }} />
                                    </Box>
                                }
                                secondary={<Typography variant="caption" color="text.secondary" noWrap>{t('hardcoded.pages_chatpage.consulta_inteligente_del_sistema')}</Typography>}
                            />
                        </ListItemButton>
                        <Divider sx={{ my: 0.5 }} />

                        {/* Dos grupos con encabezado pegajoso: al bajar por una lista
                            larga se sigue viendo en cuál se está. */}
                        {[
                            { clave: 'directos', titulo: t('hardcoded.pages_chatpage.mensajes_directos'), items: channelGroups.directos },
                            { clave: 'canales', titulo: t('hardcoded.pages_chatpage.canales'), items: channelGroups.canales },
                        ].filter((g) => g.items.length > 0).map((grupo) => (
                            <React.Fragment key={grupo.clave}>
                                <ListSubheader disableSticky={false} sx={{
                                    bgcolor: 'background.paper', lineHeight: '28px', fontSize: '0.68rem',
                                    fontWeight: 800, letterSpacing: '.06em', color: 'text.secondary',
                                }}>
                                    {grupo.titulo.toUpperCase()} · {grupo.items.length}
                                </ListSubheader>
                                {grupo.items.map((c) => {
                                    const meta = channelTypeMeta[c.channel_type] || channelTypeMeta.custom;
                                    const MetaIcon = meta.Icon;
                                    const u = getUnread(c.id);
                                    const titulo = channelTitle(c, currentUserId);
                                    const esDirecto = c.channel_type === 'direct';
                                    const otro = esDirecto ? directCounterpart(c, currentUserId) : null;
                                    return (
                                        <ListItemButton key={c.id} selected={c.id === selectedId}
                                            onClick={() => setSelectedId(c.id)}
                                            sx={{ borderRadius: 1.5, mx: 0.5, mb: 0.25 }}>
                                            <ListItemAvatar sx={{ minWidth: 42 }}>
                                                {esDirecto ? (
                                                    // Una persona se reconoce por su cara, no por un icono
                                                    // genérico igual para todos los directos.
                                                    <Avatar sx={{
                                                        width: 32, height: 32, fontSize: '0.75rem', fontWeight: 700,
                                                        bgcolor: colorForUser(otro?.user_id || titulo),
                                                    }}>
                                                        {initialsFor(titulo)}
                                                    </Avatar>
                                                ) : (
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'action.hover' }}>
                                                        <MetaIcon fontSize="small" sx={{
                                                            color: c.channel_type === 'custom' ? 'warning.main'
                                                                : c.channel_type === 'support' ? 'primary.main' : 'text.secondary',
                                                        }} />
                                                    </Avatar>
                                                )}
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography variant="body2" noWrap sx={{ fontWeight: u.count > 0 ? 800 : 500, flex: 1 }}>
                                                            {titulo}
                                                        </Typography>
                                                        {chatPrefs.isChannelMuted(c.id) && (
                                                            <MutedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                                        )}
                                                        {u.mentions > 0 && (
                                                            <Chip size="small" icon={<AtIcon sx={{ fontSize: 12 }} />} label={u.mentions}
                                                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 800, bgcolor: 'primary.main', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }} />
                                                        )}
                                                        {u.count > 0 && (
                                                            <Chip size="small" label={u.count} color="error" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                        {esDirecto ? (otro?.email || meta.label) : meta.label}
                                                        {c.last_message_at ? ` · ${formatDay(c.last_message_at)}` : ''}
                                                    </Typography>
                                                }
                                            />
                                        </ListItemButton>
                                    );
                                })}
                            </React.Fragment>
                        ))}

                        {/* Un filtro sin resultados NO es "no tienes canales". */}
                        {channelGroups.hayFiltro && channelGroups.totalVisible === 0 && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('hardcoded.pages_chatpage.sin_conversaciones_para_esa_busqueda')}
                                </Typography>
                            </Box>
                        )}
                        })}
                        {/* Estados diferenciados: cargando / error / vacío.
                            Solo mostramos "no hay canales" si la carga terminó SIN error. */}
                        {!channels.length && channelsLoading && (
                            <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}><CircularProgress size={18} /></Box>
                        )}
                        {!channels.length && !channelsLoading && channelsError && (
                            <Box sx={{ p: 1.5 }}>
                                <Alert
                                    severity="warning"
                                    action={
                                        <Button color="inherit" size="small" onClick={() => { setChannelsLoading(true); loadChannels(); }}>
                                            {i18n.t('hardcoded.components_ai_airecommendationspanel.reintentar')}</Button>
                                    }
                                >
                                    {t('hardcoded.pages_chatpage.no_se_pudieron_cargar_los')}</Alert>
                            </Box>
                        )}
                        {!channels.length && !channelsLoading && !channelsError && (
                            <Box sx={{ p: 1.5 }}><Alert severity="info">{t('hardcoded.pages_chatpage.aun_no_hay_canales')}</Alert></Box>
                        )}
                    </List>
                </Paper>

                {/* Panel mensajes — en móvil se oculta si NO hay canal seleccionado */}
                <Paper
                    sx={{ display: isMobile && !selectedId ? 'none' : 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'grey.50', position: 'relative', minWidth: 0, minHeight: 0 }}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
                    onDrop={(e) => {
                        e.preventDefault(); setDragOver(false);
                        if (selectedChannel && e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
                    }}
                >
                    {dragOver && selectedChannel && (
                        <Box sx={{ position: 'absolute', inset: 0, zIndex: 10, bgcolor: 'rgba(211,47,47,0.08)', border: '2px dashed', borderColor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                            <Typography variant="h6" color="primary.main">{t('hardcoded.pages_chatpage.suelta_los_archivos_para_adjuntarlos')}</Typography>
                        </Box>
                    )}
                    {selectedId === AI_CHANNEL_ID ? (
                        <>
                            <Box sx={{ px: 1.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.25,
                                color: '#fff', background: 'linear-gradient(135deg, #1a1a1a 0%, #b71c1c 100%)' }}>
                                {isMobile && (
                                    <IconButton size="small" edge="start" onClick={() => setSelectedId(null)} sx={{ color: '#fff', mr: 0.25 }}>
                                        <ArrowBackIcon fontSize="small" />
                                    </IconButton>
                                )}
                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.18)' }}><AssistantIcon sx={{ fontSize: 20 }} /></Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" fontWeight={800} lineHeight={1.1} noWrap>{i18n.t('hardcoded.components_ai_assistantlauncher.asistente_aps')}</Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.85 }}>{t('hardcoded.pages_chatpage.ia_especializada_en_tu_operacion')}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ flex: 1, minHeight: 0 }}>
                                <AssistantPanel embedded />
                            </Box>
                        </>
                    ) : selectedChannel ? (
                        <>
                            <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                    {/* Botón volver SOLO en móvil: limpia el canal y vuelve a la lista */}
                                    {isMobile && (
                                        <Tooltip title={t('hardcoded.pages_chatpage.volver_a_canales')}>
                                            <IconButton size="small" edge="start" onClick={() => setSelectedId(null)} sx={{ mr: 0.25 }}>
                                                <ArrowBackIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {selMeta && <selMeta.Icon fontSize="small" sx={{ color: selectedChannel.channel_type === 'custom' ? 'warning.main' : 'primary.main', flexShrink: 0 }} />}
                                    <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>{channelTitle(selectedChannel, currentUserId)}</Typography>
                                    <Tooltip title={t('hardcoded.pages_chatpage.iniciar_llamada_de_voz')}>
                                        <IconButton size="small" color="primary" onClick={() => startCall('audio')}><CallIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('hardcoded.pages_chatpage.iniciar_videollamada')}>
                                        <IconButton size="small" color="primary" onClick={() => startCall('video')}><VideoCallIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    <ChannelMuteMenu channelId={selectedChannel.id} />
                                    {/* El chip de tipo de canal ocupa espacio: lo ocultamos en móvil
                                        (el scope ya aparece en la línea inferior). */}
                                    {!isMobile && (
                                        <Tooltip title={selMeta?.scope || ''}>
                                            <Chip size="small" label={selMeta?.label || selectedChannel.channel_type} variant="outlined"
                                                color={selectedChannel.channel_type === 'support' ? 'primary' : selectedChannel.channel_type === 'custom' ? 'warning' : 'default'} />
                                        </Tooltip>
                                    )}
                                    {canDeleteMap[selectedChannel.id] && (
                                        <Tooltip title={t('hardcoded.pages_chatpage.eliminar_canal_y_todo_su')}>
                                            <IconButton size="small" color="error" onClick={() => setDeleteConfirm(selectedChannel)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    {selMeta?.scope}{selectedChannel.description ? ` — ${selectedChannel.description}` : ''}
                                </Typography>
                                {/* Banner de llamada activa en el canal */}
                                {channelCall && !activeCall && (
                                    <Alert
                                        icon={<ActiveCallIcon />}
                                        severity="success"
                                        sx={{ mt: 1, py: 0, alignItems: 'center' }}
                                        action={<Button size="small" variant="contained" color="success" onClick={joinChannelCall}>{t('pages_chatpage.button_join')}</Button>}
                                    >
                                        {channelCall.call_type === 'video' ? t('hardcoded.components_chat_calloverlay.videollamada') : t('hardcoded.components_chat_calloverlay.llamada')} {t('hardcoded.pages_chatpage.en_curso')}{(channelCall.participants || []).length} {i18n.t('hardcoded.pages_chatpage.participantes')}</Alert>
                                )}
                            </Box>

                            <Box ref={messagesScrollRef} onScroll={handleScroll} sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: { xs: 1.25, sm: 2 }, position: 'relative' }}>
                                {loadingMsgs && <CircularProgress />}
                                {!loadingMsgs && !messages.length && (
                                    <Box sx={{ textAlign: 'center', mt: 6, color: 'text.secondary' }}>
                                        <DotIcon sx={{ opacity: 0.3, fontSize: 60 }} />
                                        <Typography variant="body2">{t('hardcoded.pages_chatpage.sin_mensajes_aun_escribe_el')}</Typography>
                                    </Box>
                                )}
                                {messagesByDay.map((item) => {
                                    if (item.type === 'separator') {
                                        return (
                                            <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', my: 2, gap: 1 }}>
                                                <Divider sx={{ flex: 1 }} />
                                                <Chip size="small" label={item.label} sx={{ bgcolor: 'background.paper' }} />
                                                <Divider sx={{ flex: 1 }} />
                                            </Box>
                                        );
                                    }
                                    const isOwn = String(item.message.user_id) === String(currentUserId);
                                    return (
                                        <MessageBubble
                                            key={item.key}
                                            message={item.message}
                                            isOwn={isOwn}
                                            showAvatar={item.showAvatar}
                                            showName={item.showName}
                                            onNavigate={navigate}
                                            currentUserId={currentUserId}
                                            onReact={toggleReaction}
                                            onReply={startReply}
                                            onJump={jumpToMessage}
                                            highlighted={String(highlightId) === String(item.message.id)}
                                            messageStatus={isOwn ? messageStatusMap[item.message.id || item.message.client_message_id] : undefined}
                                            onShowInfo={isOwn ? handleShowInfo : undefined}
                                            isMobile={isMobile}
                                        />
                                    );
                                })}
                                {typingList.length > 0 && (
                                    <TypingIndicator names={typingList} />
                                )}
                            </Box>

                            {/* Botón flotante "ir al final" con badge de no leídos */}
                            <AnimatePresence>
                                {!atBottom && (
                                    <Box
                                        component={motion.div}
                                        initial={{ opacity: 0, scale: 0.8, y: 8 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.8, y: 8 }}
                                        sx={{ position: 'absolute', right: 18, bottom: 92, zIndex: 8 }}
                                    >
                                        <Badge badgeContent={newSinceScroll} color="error" overlap="circular"
                                            sx={{ '& .MuiBadge-badge': { fontWeight: 700 } }}>
                                            <IconButton onClick={scrollToBottom}
                                                sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', boxShadow: '0 3px 10px rgba(0,0,0,0.18)', '&:hover': { bgcolor: 'grey.100' } }}>
                                                <ArrowDownIcon />
                                            </IconButton>
                                        </Badge>
                                    </Box>
                                )}
                            </AnimatePresence>

                            <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                                {/* Barra de cita: respondiendo a un mensaje */}
                                {replyTo && (
                                    <Box
                                        component={motion.div}
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1, mb: 1,
                                            px: 1, py: 0.75, borderRadius: 1.5,
                                            borderLeft: '3px solid', borderColor: 'primary.main',
                                            bgcolor: 'rgba(211,47,47,0.06)',
                                        }}
                                    >
                                        <ReplyIcon fontSize="small" color="primary" />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main', display: 'block', lineHeight: 1.2 }}>
                                                {i18n.t('hardcoded.pages_chatpage.respondiendo_a')}{replyTo.user_name || replyTo.user_email || t('audit.table.user')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                {replyTo.body || (replyTo.attachments?.length ? t('hardcoded.pages_chatpage.adjunto') : '…')}
                                            </Typography>
                                        </Box>
                                        <IconButton size="small" onClick={() => setReplyTo(null)}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                                {/* Archivos en cola */}
                                {!!stagedFiles.length && (
                                    <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                                        {stagedFiles.map((f, i) => (
                                            <Chip key={`${f.name}-${i}`} size="small" icon={<FileIcon />}
                                                label={`${f.name} (${fmtSize(f.size)})`}
                                                onDelete={() => setStagedFiles((p) => p.filter((_, j) => j !== i))} />
                                        ))}
                                    </Stack>
                                )}
                                {uploading && (
                                    <Box sx={{ mb: 1 }}>
                                        <LinearProgress variant={uploadPct > 0 ? 'determinate' : 'indeterminate'} value={uploadPct} />
                                        <Typography variant="caption" color="text.secondary">{i18n.t('hardcoded.pages_chatpage.subiendo')}{uploadPct}%</Typography>
                                    </Box>
                                )}
                                <Stack direction="row" spacing={{ xs: 0.25, sm: 1 }} alignItems="flex-end" sx={{ minWidth: 0 }}>
                                    <input
                                        type="file" multiple hidden ref={fileInputRef}
                                        accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,.mp3,.m4a,.wav,.ogg,.webm,.mp4,.mov"
                                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                                    />
                                    {!recordingActive && (
                                        <Tooltip title={t('hardcoded.pages_chatpage.adjuntar_archivo')}>
                                            <span>
                                                <IconButton color="primary" size={isMobile ? 'small' : 'medium'} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                                                    <AttachIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    {!recordingActive && videoRecordingSupported() && (
                                        <Tooltip title={t('hardcoded.pages_chatpage.grabar_nota_de_video')}>
                                            <span>
                                                <IconButton color="primary" size={isMobile ? 'small' : 'medium'} onClick={() => setVideoNoteOpen(true)} disabled={uploading}>
                                                    <VideoCallIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    <VoiceRecorder onSend={sendVoice} onStateChange={setRecordingActive} disabled={uploading} />
                                    {!recordingActive && (
                                        <>
                                            <TextField
                                                fullWidth size="small"
                                                placeholder={t('hardcoded.pages_chatpage.mensaje_en_name_usa_para', { name: channelTitle(selectedChannel, currentUserId) })}
                                                value={body} onChange={handleInput}
                                                inputRef={inputRef}
                                                multiline maxRows={5}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Escape' && mentionOpen) { setMentionOpen(false); return; }
                                                    if (e.key === 'Enter' && !e.shiftKey && !mentionOpen) {
                                                        e.preventDefault();
                                                        if (!sending) sendMessage();
                                                    }
                                                }}
                                            />
                                            <Button variant="contained" onClick={sendMessage}
                                                sx={{ minWidth: { xs: 44, sm: 64 }, px: { xs: 1, sm: 2 }, flexShrink: 0 }}
                                                disabled={sending || uploading || (!body.trim() && !stagedFiles.length)}>
                                                {sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                            </Button>
                                        </>
                                    )}
                                </Stack>
                                <MentionAutocomplete open={mentionOpen} anchorEl={mentionAnchor} query={mentionQuery}
                                    channelId={selectedId} onPick={insertMention} onClose={() => setMentionOpen(false)} />
                                {!!pendingMentions.length && (
                                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                                        {pendingMentions.map((m, i) => (
                                            <Chip key={i} size="small" label={`@${m.label}`} variant="outlined" color="primary" />
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center', m: 'auto' }}>
                            <Alert severity="info">{t('hardcoded.pages_chatpage.selecciona_o_crea_un_canal')}</Alert>
                        </Box>
                    )}
                </Paper>
            </Box>

            <DirectChatDialog
                open={directOpen}
                onClose={() => setDirectOpen(false)}
                currentUserId={currentUserId}
                onOpened={(canal) => {
                    // Si ya existía, puede no estar aún en la lista de esta pestaña
                    // (se creó desde otra sesión): se refresca y se entra igual.
                    loadChannels();
                    setSelectedId(canal.id);
                }}
            />

            <ChannelCreateDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={() => loadChannels()}
                companyId={currentCompany?.company_id || currentCompany?.id}
            />

            {/* Confirmación de borrado */}
            <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: 'error.main' }}>{t('hardcoded.pages_chatpage.eliminar_canal')}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" gutterBottom>
                        {t('hardcoded.pages_chatpage.seguro_que_quieres_eliminar_el')}<strong>#{deleteConfirm?.name}</strong>?
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        {t('hardcoded.pages_chatpage.esta_accion_es_irreversible_se')}</Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>{t('common.cancel')}</Button>
                    <Button color="error" variant="contained" onClick={handleDeleteChannel} startIcon={<DeleteIcon />}>
                        {t('audit.actions.DELETE')}</Button>
                </DialogActions>
            </Dialog>

            {/* Info del mensaje (entregado/visto por usuario), estilo WhatsApp */}
            <Popover
                open={!!infoPopover}
                anchorEl={infoPopover?.anchorEl || null}
                onClose={() => setInfoPopover(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                {infoPopover && (
                    <MessageInfoContent
                        message={infoPopover.message}
                        receipts={receipts}
                        otherIds={otherMemberIds}
                    />
                )}
            </Popover>

            {/* Preferencias de notificaciones */}
            <ChatPreferencesDialog open={prefsOpen} onClose={() => setPrefsOpen(false)} />

            {/* Grabador de notas de video */}
            <VideoNoteRecorder
                open={videoNoteOpen}
                onClose={() => setVideoNoteOpen(false)}
                onSend={sendVideoNote}
            />

            {/* Llamada entrante (con tono y expiración) */}
            <IncomingCallDialog
                invite={incomingInvite}
                onAccept={acceptIncoming}
                onReject={rejectIncoming}
            />

            {/* Overlay de llamada activa */}
            {activeCall && (
                <CallOverlay
                    call={activeCall.call}
                    iceServers={activeCall.iceServers}
                    ringTimeout={activeCall.ringTimeout || 45}
                    currentUserId={currentUserId}
                    userNamesById={userNamesById}
                    onClosed={(endStatus) => {
                        setActiveCall(null);
                        if (endStatus === 'missed') showInfo(t('hardcoded.pages_chatpage.sin_respuesta_llamada_finalizada'));
                        if (selectedId) {
                            api.chat.activeCall(selectedId)
                                .then((r) => setChannelCall(r.data?.call || null))
                                .catch(() => setChannelCall(null));
                        }
                    }}
                />
            )}
        </Box>
    );
};

export default ChatPage;
